<template>
  <v-container fluid class="pbx-container px-3">
    <!-- Info Alert about Enterprise App -->
    <div class="enterprise-app-notice mb-6 mx-0">
      <div class="notice-content">
        <div class="notice-icon-wrapper">
          <v-icon size="24" color="#FFFFFF">mdi-cellphone-arrow-down</v-icon>
        </div>
        <div class="notice-text-wrapper">
          <div class="notice-title">
            <span class="notice-badge">Coming Soon</span>
            Mobile App Integration
          </div>
          <div class="notice-description">
            All GoDial PBX features will be available on the GoDial Enterprise
            mobile app. We're actively working to bring you a seamless mobile
            experience.
          </div>
        </div>
        <div class="notice-status">
          <div class="status-indicator">
            <span class="status-pulse"></span>
            <span class="status-text">In Development</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modern Header -->
    <div v-if="showHeader" class="header-section mb-6">
      <!-- Close Button -->
      <v-btn
        icon
        small
        class="header-close-btn"
        style="position: absolute; top: 12px; right: 12px; background: rgba(255,255,255,0.2); z-index: 1;"
        @click="dismissHeader"
      >
        <v-icon color="white" size="18">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-phone-voip</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">GoDial PBX</h1>
          <p class="page-subtitle mb-0">
            Enterprise-grade cloud telephony with your own SIP carrier
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-check-circle</v-icon>
            Flexible
          </v-chip>
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-shield-check</v-icon>
            Secure
          </v-chip>
          <v-chip
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-chart-line</v-icon>
            Scalable
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-cloud-check-outline</v-icon
            >
            <span>Own Carriers</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-devices</v-icon>
            <span>Multi-Platform</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-record-rec</v-icon>
            <span>Call Recording</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-sitemap-outline</v-icon
            >
            <span>IVR (Soon)</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-earth</v-icon>
            <span>Global Calling</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Setup Options Section - Twilio vs Custom -->
    <v-row v-if="!preloader && !isCarrierConfigured" class="ma-0 mb-6">
      <v-col cols="12" class="pa-2">
        <v-card class="setup-options-card" elevation="0">
          <v-card-text class="pa-6 pb-8">
            <div class="text-center mb-6">
              <div class="step-badge mb-3">
                <v-chip color="primary" small outlined class="font-weight-bold">
                  <v-icon x-small left>mdi-phone-voip</v-icon>
                  GET STARTED
                </v-chip>
              </div>
              <h2 class="setup-options-title mb-2">Choose Your Setup Method</h2>
              <p class="setup-options-subtitle">
                Connect with Twilio for quick setup or configure your own SIP
                carrier
              </p>
            </div>

            <v-row justify="center" align="center">
              <!-- Twilio Option -->
              <v-col cols="12" sm="5" md="5" lg="4">
                <v-card
                  class="setup-option-card twilio-card"
                  elevation="0"
                  style="border-radius: 28px !important;"
                  @click="showTwilioAuthDialog = true"
                >
                  <v-card-text class="pa-5 text-center">
                    <div class="option-icon-wrapper twilio-icon mx-auto mb-4">
                      <v-icon size="32" color="white">mdi-phone-sync</v-icon>
                    </div>
                    <h3 class="option-title mb-2">Connect with Twilio</h3>
                    <v-chip
                      color="#F22F46"
                      x-small
                      text-color="white"
                      class="mb-4"
                    >
                      <v-icon x-small left>mdi-lightning-bolt</v-icon>
                      Quick Setup
                    </v-chip>

                    <div class="option-features">
                      <div class="feature-item">
                        <v-icon color="#F22F46" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>One-click OAuth connection</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#F22F46" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Auto-configure SIP trunks</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#F22F46" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Manage phone numbers</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#F22F46" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Built-in call routing</span>
                      </div>
                    </div>

                    <v-btn
                      color="#F22F46"
                      block
                      depressed
                      dark
                      class="mt-5 setup-btn"
                      @click.stop="showTwilioAuthDialog = true"
                    >
                      <v-icon left small>mdi-link-variant</v-icon>
                      Connect with Twilio
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- OR Divider -->
              <v-col
                cols="12"
                sm="2"
                md="2"
                lg="1"
                class="d-flex justify-center align-center"
              >
                <div class="or-divider">
                  <div class="or-line"></div>
                  <div class="or-circle">
                    <span>OR</span>
                  </div>
                  <div class="or-line"></div>
                </div>
              </v-col>

              <!-- Custom SIP Option -->
              <v-col cols="12" sm="5" md="5" lg="4">
                <v-card
                  class="setup-option-card custom-card"
                  elevation="0"
                  style="border-radius: 28px !important;"
                  @click="openCarrierDialog"
                >
                  <v-card-text class="pa-5 text-center">
                    <div class="option-icon-wrapper custom-icon mx-auto mb-4">
                      <v-icon size="32" color="white">mdi-cog</v-icon>
                    </div>
                    <h3 class="option-title mb-2">Custom SIP Setup</h3>
                    <v-chip
                      color="#37cf37"
                      x-small
                      text-color="white"
                      class="mb-4"
                    >
                      <v-icon x-small left>mdi-tune</v-icon>
                      Full Control
                    </v-chip>

                    <div class="option-features">
                      <div class="feature-item">
                        <v-icon color="#37cf37" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Use any SIP carrier</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#37cf37" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Manual trunk configuration</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#37cf37" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Custom SIP settings</span>
                      </div>
                      <div class="feature-item">
                        <v-icon color="#37cf37" small class="mr-2"
                          >mdi-check-circle</v-icon
                        >
                        <span>Advanced routing options</span>
                      </div>
                    </div>

                    <v-btn
                      color="#37cf37"
                      block
                      depressed
                      dark
                      class="mt-5 setup-btn"
                      @click.stop="openCarrierDialog"
                    >
                      <v-icon left small>mdi-cog</v-icon>
                      Configure Custom SIP
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-alert color="primary" dense text class="mt-6 mb-0 info-alert">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-3"
                  >mdi-lightbulb-outline</v-icon
                >
                <div>
                  <strong>Not sure which to choose?</strong>
                  <span class="ml-1"
                    >Twilio is recommended for quick setup. Custom SIP is ideal
                    if you already have a carrier.</span
                  >
                </div>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Grid -->
    <v-row v-if="!preloader" class="ma-0">
      <!-- Carrier Section -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card class="modern-card" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-phone-settings</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">Carrier Configuration</h3>
                <p class="card-subtitle mb-0">SIP trunk settings</p>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-switch
                v-model="carrierEnabledLocal"
                :label="carrierEnabledLocal ? 'Active' : 'Inactive'"
                hide-details
                dense
                inset
                color="success"
                class="mr-3 mt-0"
                @change="onToggleCarrierEnabled"
              ></v-switch>
              <v-btn icon small color="primary" @click="openCarrierDialog">
                <v-icon>mdi-cog</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <div v-if="isCarrierConfigured">
              <v-row dense class="mb-3">
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Carrier Name</div>
                    <div class="info-value">
                      {{ carrierSettings.name || "Not set" }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">SIP Host</div>
                    <div class="info-value">
                      {{ carrierSettings.host || "Not set" }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Port</div>
                    <div class="info-value">
                      {{ carrierSettings.port || "Not set" }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Username</div>
                    <div class="info-value">
                      {{ carrierSettings.username || "Not set" }}
                    </div>
                  </div>
                </v-col>
                <v-col v-if="carrierSettings.prefix" cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">Tech Prefix</div>
                    <div class="info-value">
                      {{ carrierSettings.prefix }}
                    </div>
                  </div>
                </v-col>
                <v-col
                  v-if="
                    carrierSettings.transport &&
                      carrierSettings.transport !== 'udp'
                  "
                  cols="12"
                  sm="6"
                >
                  <div class="info-item">
                    <div class="info-label">Transport</div>
                    <div class="info-value" style="text-transform: uppercase;">
                      {{ carrierSettings.transport }}
                    </div>
                  </div>
                </v-col>
                <v-col v-if="carrierSettings.srtpEnabled" cols="12" sm="6">
                  <div class="info-item">
                    <div class="info-label">SRTP</div>
                    <div class="info-value">
                      <v-icon small color="success" class="mr-1"
                        >mdi-shield-check</v-icon
                      >
                      Enabled
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-alert
                :color="carrierEnabledLocal ? 'success' : 'grey'"
                text
                dense
                class="mb-0"
              >
                <div class="d-flex align-center">
                  <v-icon
                    :color="carrierEnabledLocal ? 'success' : 'grey'"
                    class="mr-2"
                  >
                    {{
                      carrierEnabledLocal
                        ? "mdi-check-circle"
                        : "mdi-minus-circle"
                    }}
                  </v-icon>
                  <span
                    >Carrier is
                    {{
                      carrierEnabledLocal ? "active and ready" : "inactive"
                    }}</span
                  >
                </div>
              </v-alert>
            </div>
            <div v-else class="text-center py-6">
              <v-icon size="48" color="grey lighten-1" class="mb-3"
                >mdi-account-network-outline</v-icon
              >
              <p class="mb-2 grey--text">No carrier configured</p>
              <p class="caption grey--text">
                Click configure to add your SIP carrier details
              </p>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4 carrier-card-actions">
            <v-btn
              outlined
              color="primary"
              class="action-btn-mobile"
              @click="testCarrierClick"
            >
              <v-icon left small>mdi-flask-outline</v-icon>
              Test Connection
            </v-btn>
            <v-btn
              v-if="isCarrierConfigured"
              outlined
              color="error"
              class="action-btn-mobile"
              @click="deletePBXConfiguration"
            >
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
            <v-spacer class="hidden-xs-only"></v-spacer>
            <v-btn
              color="primary"
              depressed
              class="action-btn-mobile"
              @click="openCarrierDialog"
            >
              <v-icon left small>mdi-cog</v-icon>
              {{ isCarrierConfigured ? "Update" : "Configure" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- DID Section -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card class="modern-card" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge icon-badge-green mr-3">
                <v-icon color="#FFFFFF">mdi-phone-plus</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">DID Numbers</h3>
                <p class="card-subtitle mb-0">Manage inbound numbers</p>
              </div>
            </div>
            <v-btn icon small color="success" @click="openDIDDialog">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <div v-if="existingDIDs && existingDIDs.length > 0">
              <div class="did-stats mb-3">
                <div class="stat-badge">
                  <v-icon color="success" small class="mr-1">mdi-phone</v-icon>
                  <span
                    >{{ existingDIDs.length }} DID{{
                      existingDIDs.length > 1 ? "s" : ""
                    }}</span
                  >
                </div>
              </div>

              <div class="did-list">
                <div
                  v-for="(did, idx) in existingDIDs.slice(0, 4)"
                  :key="did.id || idx"
                  class="did-item"
                >
                  <div class="d-flex align-center">
                    <v-icon color="primary" small class="mr-2"
                      >mdi-numeric</v-icon
                    >
                    <div class="flex-grow-1">
                      <div class="did-number">{{ did.number }}</div>
                      <div class="did-desc">
                        {{ did.description || "No description" }}
                      </div>
                    </div>
                    <v-chip
                      v-if="did.routing && did.routing.type"
                      :color="getRoutingColor(did.routing.type)"
                      x-small
                      text-color="white"
                    >
                      <v-icon x-small left>{{
                        getRoutingIcon(did.routing.type)
                      }}</v-icon>
                      {{ getRoutingStrategyLabel(did.routing.type) }}
                    </v-chip>
                  </div>
                </div>
                <div v-if="existingDIDs.length > 4" class="text-center pt-2">
                  <v-btn text x-small color="primary" @click="openDIDDialog">
                    +{{ existingDIDs.length - 4 }} more
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-6">
              <v-icon size="48" color="grey lighten-1" class="mb-3"
                >mdi-phone-off-outline</v-icon
              >
              <p class="mb-2 grey--text">No DID numbers configured</p>
              <p class="caption grey--text">
                Add DID numbers to receive incoming calls
              </p>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="success" depressed @click="openDIDDialog">
              <v-icon left small>mdi-plus</v-icon>
              Manage DIDs
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- IVR System -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card class="modern-card coming-soon-card" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge icon-badge-disabled mr-3">
                <v-icon color="#FFFFFF">mdi-sitemap-outline</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">IVR System</h3>
                <p class="card-subtitle mb-0">Interactive Voice Response</p>
              </div>
            </div>
            <v-chip color="orange" small text-color="white">
              <v-icon x-small left>mdi-clock-outline</v-icon>
              Coming Soon
            </v-chip>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="feature-box">
                  <v-icon color="grey lighten-1" class="mb-2"
                    >mdi-phone-in-talk</v-icon
                  >
                  <div class="feature-box-title">Call Flows</div>
                  <div class="feature-box-desc">Design custom call routing</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="feature-box">
                  <v-icon color="grey lighten-1" class="mb-2">mdi-menu</v-icon>
                  <div class="feature-box-title">IVR Menus</div>
                  <div class="feature-box-desc">Multi-level menu options</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="feature-box">
                  <v-icon color="grey lighten-1" class="mb-2"
                    >mdi-clock-time-four-outline</v-icon
                  >
                  <div class="feature-box-title">Business Hours</div>
                  <div class="feature-box-desc">Time-based routing</div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="feature-box">
                  <v-icon color="grey lighten-1" size="32" class="mb-2"
                    >mdi-microphone</v-icon
                  >
                  <div class="feature-box-title">Voice Prompts</div>
                  <div class="feature-box-desc">Custom greetings</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Settings Section -->
      <v-col cols="12" lg="6" class="pa-2">
        <v-card class="modern-card" elevation="0">
          <div class="card-header justify-start">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-cog-outline</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">Settings</h3>
                <p class="card-subtitle mb-0">Application configuration</p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <div class="settings-list">
              <!-- Web Dialer Settings Item -->
              <div class="settings-list-item web-dialer-card">
                <div class="settings-item-header">
                  <div class="d-flex align-center">
                    <div class="dialer-icon-wrapper mr-3">
                      <v-icon color="white" size="24">mdi-phone-voip</v-icon>
                    </div>
                    <div>
                      <h3 class="settings-item-title mb-0">Web Dialer</h3>
                      <p
                        class="settings-item-subtitle mb-0 text-caption grey--text"
                      >
                        Browser-based VoIP calling
                      </p>
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <v-chip
                      :color="
                        dialerConnectionStatus === 'registered'
                          ? 'success'
                          : dialerConnectionStatus === 'connecting'
                          ? 'warning'
                          : 'grey'
                      "
                      small
                      :outlined="dialerConnectionStatus !== 'registered'"
                      :text-color="
                        dialerConnectionStatus === 'registered'
                          ? 'white'
                          : undefined
                      "
                    >
                      <v-icon x-small left>
                        {{
                          dialerConnectionStatus === "registered"
                            ? "mdi-check-circle"
                            : dialerConnectionStatus === "connecting"
                            ? "mdi-loading mdi-spin"
                            : "mdi-circle-outline"
                        }}
                      </v-icon>
                      {{ dialerConnectionText }}
                    </v-chip>
                  </div>
                </div>

                <div
                  class="dialer-status-bar"
                  :class="
                    dialerConnectionStatus === 'registered'
                      ? 'status-connected'
                      : 'status-disconnected'
                  "
                >
                  <v-icon x-small class="mr-1">{{
                    dialerConnectionStatus === "registered"
                      ? "mdi-wifi"
                      : "mdi-wifi-off"
                  }}</v-icon>
                  <span class="text-caption">
                    {{
                      dialerConnectionStatus === "registered"
                        ? "Connected to " + sipConfig.domain
                        : "Not connected"
                    }}
                  </span>
                </div>

                <div class="settings-item-content pa-4">
                  <!-- Connection Details -->
                  <div class="dialer-details-grid mb-4">
                    <div class="detail-card">
                      <div class="detail-icon">
                        <v-icon small color="primary">mdi-server</v-icon>
                      </div>
                      <div class="detail-content">
                        <span class="detail-label">SIP Server</span>
                        <span class="detail-value">{{ sipConfig.domain }}</span>
                      </div>
                    </div>
                    <div class="detail-card">
                      <div class="detail-icon">
                        <v-icon small color="primary">mdi-account</v-icon>
                      </div>
                      <div class="detail-content">
                        <span class="detail-label">Account</span>
                        <span class="detail-value">{{
                          currentUser.username ||
                            currentUser.email ||
                            "Not logged in"
                        }}</span>
                      </div>
                    </div>
                    <div class="detail-card">
                      <div class="detail-icon">
                        <v-icon small color="primary">mdi-web</v-icon>
                      </div>
                      <div class="detail-content">
                        <span class="detail-label">WebSocket</span>
                        <span
                          class="detail-value text-truncate"
                          style="max-width: 180px;"
                          >{{ sipConfig.wsServers }}</span
                        >
                      </div>
                    </div>
                    <div class="detail-card">
                      <div class="detail-icon">
                        <v-icon small color="primary">mdi-key</v-icon>
                      </div>
                      <div class="detail-content">
                        <span class="detail-label">Auth</span>
                        <span class="detail-value">
                          <v-chip
                            v-if="currentUser.token"
                            x-small
                            color="success"
                            text-color="white"
                          >
                            <v-icon x-small left>mdi-check</v-icon>Token
                          </v-chip>
                          <v-chip
                            v-else
                            x-small
                            color="error"
                            text-color="white"
                          >
                            <v-icon x-small left>mdi-close</v-icon>Missing
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Active Call Banner -->
                  <div v-if="isInCall" class="active-call-banner mb-3">
                    <div class="call-pulse"></div>
                    <v-icon color="white" class="mr-2"
                      >mdi-phone-in-talk</v-icon
                    >
                    <div class="call-info">
                      <span class="call-number">{{ currentNumber }}</span>
                      <span class="call-duration">{{
                        formatDuration(callDuration)
                      }}</span>
                    </div>
                  </div>

                  <!-- Connection Error Alert -->
                  <v-alert
                    v-if="dialerConnectionStatus === 'error' && connectionError"
                    type="error"
                    dense
                    text
                    class="mb-3"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="error" small class="mr-2"
                        >mdi-alert-circle</v-icon
                      >
                      <span class="text-body-2">{{ connectionError }}</span>
                    </div>
                  </v-alert>

                  <!-- Connection Progress -->
                  <div
                    v-if="dialerConnectionStatus === 'connecting'"
                    class="connection-progress mb-3"
                  >
                    <v-progress-linear
                      indeterminate
                      color="primary"
                      height="3"
                      class="mb-2"
                    ></v-progress-linear>
                    <div class="d-flex align-center justify-center">
                      <v-icon small color="primary" class="mr-2 mdi-spin"
                        >mdi-loading</v-icon
                      >
                      <span class="text-caption grey--text">{{
                        connectionProgress || "Connecting..."
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="dialer-actions pa-4 pt-0">
                  <!-- Connecting State - Show Cancel Button -->
                  <div
                    v-if="dialerConnectionStatus === 'connecting'"
                    class="d-flex"
                    style="gap: 8px;"
                  >
                    <v-btn color="grey" outlined class="flex-grow-1" disabled>
                      <v-icon left class="mdi-spin">mdi-loading</v-icon>
                      Connecting...
                    </v-btn>
                    <v-btn
                      color="error"
                      depressed
                      @click="cancelDialerConnection"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>

                  <!-- Disconnected or Error State - Show Connect Button -->
                  <v-btn
                    v-else-if="dialerConnectionStatus !== 'registered'"
                    color="primary"
                    depressed
                    block
                    class="mb-2"
                    @click="connectDialer"
                  >
                    <v-icon left>mdi-power</v-icon>
                    {{
                      dialerConnectionStatus === "error"
                        ? "Retry Connection"
                        : "Connect to SIP Server"
                    }}
                  </v-btn>

                  <!-- Registered State - Show Dialer & Settings -->
                  <div v-else class="d-flex" style="gap: 8px;">
                    <v-btn
                      color="success"
                      depressed
                      class="flex-grow-1"
                      @click="openDialer"
                    >
                      <v-icon left>mdi-phone</v-icon>
                      Open Dialer
                    </v-btn>
                    <v-btn outlined color="grey" @click="openDialerSettings">
                      <v-icon>mdi-cog</v-icon>
                    </v-btn>
                  </div>
                  <v-btn
                    v-if="
                      dialerConnectionStatus !== 'registered' &&
                        dialerConnectionStatus !== 'connecting'
                    "
                    text
                    small
                    color="grey"
                    class="mt-2"
                    block
                    @click="openDialerSettings"
                  >
                    <v-icon small left>mdi-bug</v-icon>
                    Diagnostics & Settings
                  </v-btn>
                </div>
              </div>

              <!-- Default Calling List Settings Item -->
              <div class="settings-list-item">
                <div class="settings-item-header">
                  <div class="d-flex align-center">
                    <div
                      class="settings-icon-wrapper settings-icon-orange mr-3"
                    >
                      <v-icon color="white" size="20"
                        >mdi-format-list-bulleted</v-icon
                      >
                    </div>
                    <div>
                      <h3 class="settings-item-title">Default Calling List</h3>
                      <p class="settings-item-subtitle">
                        Auto-save new contacts from calls
                      </p>
                    </div>
                  </div>
                  <v-chip
                    :color="defaultCallingListId ? 'success' : 'grey'"
                    small
                    text-color="white"
                  >
                    <v-icon x-small left>
                      {{ defaultCallingListId ? "mdi-check" : "mdi-alert" }}
                    </v-icon>
                    {{ defaultCallingListId ? "Set" : "Not Set" }}
                  </v-chip>
                </div>

                <v-divider></v-divider>

                <div class="settings-item-content pa-4">
                  <p class="mb-4 grey--text text--darken-1">
                    New contacts from incoming SIP calls will be automatically
                    saved to this list
                  </p>

                  <v-select
                    v-model="defaultCallingListId"
                    :items="companyLists"
                    item-text="name"
                    item-value="id"
                    label="Select Calling List"
                    outlined
                    dense
                    :disabled="!companyLists || companyLists.length === 0"
                    @change="saveDefaultCallingList"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary">mdi-format-list-bulleted</v-icon>
                    </template>
                  </v-select>

                  <v-alert color="info" text dense class="mb-0">
                    <div class="caption">
                      <v-icon x-small class="mr-1">mdi-information</v-icon>
                      All new contacts from calls will be organized in the
                      selected list
                    </div>
                  </v-alert>
                </div>

                <v-divider></v-divider>

                <div class="settings-item-actions pa-4 d-flex">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="orange"
                    depressed
                    :disabled="!defaultCallingListId"
                    @click="saveDefaultCallingList"
                  >
                    <v-icon left small>mdi-content-save</v-icon>
                    Save Selection
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Carrier Configuration Dialog -->
    <v-dialog
      v-model="carrierDialog"
      persistent
      max-width="700"
      transition="dialog-transition"
    >
      <v-card class="carrier-dialog-card" elevation="24">
        <!-- Modern Header with Gradient -->
        <div class="carrier-dialog-header">
          <div class="header-content">
            <div class="header-icon-wrapper">
              <v-icon color="white" size="28">mdi-phone-settings</v-icon>
            </div>
            <div class="header-text">
              <h2 class="dialog-title">Carrier Configuration</h2>
              <p class="dialog-subtitle">Configure your SIP trunk settings</p>
            </div>
          </div>
          <v-btn icon dark class="close-btn" @click="carrierDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Form Content with Tabs Style -->
        <v-card-text class="carrier-dialog-content pa-8">
          <!-- Connection Settings Section -->
          <div class="settings-section mb-6">
            <div class="section-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-connection</v-icon>
              <span class="section-title">Connection Details</span>
            </div>

            <v-row>
              <v-col cols="12">
                <div class="input-wrapper">
                  <label class="input-label">Carrier Name</label>
                  <v-text-field
                    v-model="carrierSettings.name"
                    placeholder="Enter carrier name"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-tag</v-icon>
                    </template>
                  </v-text-field>
                  <div class="input-hint">
                    A friendly name to identify this carrier
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="8">
                <div class="input-wrapper">
                  <label class="input-label"
                    >SIP Host <span class="required">*</span></label
                  >
                  <v-text-field
                    v-model="carrierSettings.host"
                    placeholder="sip.yourprovider.com"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-server</v-icon>
                    </template>
                  </v-text-field>
                  <div class="input-hint">
                    Your SIP server hostname or IP address
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <div class="input-wrapper">
                  <label class="input-label"
                    >Port <span class="required">*</span></label
                  >
                  <v-text-field
                    v-model="carrierSettings.port"
                    placeholder="5060"
                    outlined
                    dense
                    type="number"
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-numeric</v-icon>
                    </template>
                  </v-text-field>
                  <div class="input-hint">Default: 5060</div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Authentication Section -->
          <div class="settings-section mb-4">
            <div class="section-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-shield-key</v-icon>
              <span class="section-title">Authentication</span>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label"
                    >Username <span class="required">*</span></label
                  >
                  <v-text-field
                    v-model="carrierSettings.username"
                    placeholder="Enter username"
                    outlined
                    dense
                    hide-details="auto"
                    autocomplete="off"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-account</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label"
                    >Password <span class="required">*</span></label
                  >
                  <v-text-field
                    v-model="carrierSettings.password"
                    placeholder="Enter password"
                    outlined
                    dense
                    type="password"
                    hide-details="auto"
                    autocomplete="new-password"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-lock</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12">
                <div class="input-wrapper">
                  <label class="input-label"
                    >From Domain <span class="optional">(Optional)</span></label
                  >
                  <v-text-field
                    v-model="carrierSettings.fromDomain"
                    placeholder="domain.com"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-web</v-icon>
                    </template>
                  </v-text-field>
                  <div class="input-hint">
                    Override the domain in From header (leave empty to use
                    default)
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Advanced Settings Section -->
          <div class="settings-section mb-4">
            <div
              class="section-header mb-4"
              style="cursor: pointer;"
              @click="showAdvancedSettings = !showAdvancedSettings"
            >
              <v-icon color="primary" class="mr-2">mdi-tune-variant</v-icon>
              <span class="section-title">Advanced Settings</span>
              <v-spacer></v-spacer>
              <v-icon color="grey" size="20">
                {{
                  showAdvancedSettings ? "mdi-chevron-up" : "mdi-chevron-down"
                }}
              </v-icon>
            </div>

            <v-expand-transition>
              <div v-show="showAdvancedSettings">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="input-wrapper">
                      <label class="input-label">Tech Prefix</label>
                      <v-text-field
                        v-model="carrierSettings.prefix"
                        placeholder="e.g. 45454"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20">mdi-dialpad</v-icon>
                        </template>
                      </v-text-field>
                      <div class="input-hint">
                        Prepended to all dialed numbers (e.g. tech prefix from
                        your carrier)
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="input-wrapper">
                      <label class="input-label">Transport</label>
                      <v-select
                        v-model="carrierSettings.transport"
                        :items="[
                          { text: 'UDP', value: 'udp' },
                          { text: 'TCP', value: 'tcp' },
                          { text: 'TLS', value: 'tls' },
                        ]"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20"
                            >mdi-swap-vertical</v-icon
                          >
                        </template>
                      </v-select>
                      <div class="input-hint">
                        SIP transport protocol (default: UDP)
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="input-wrapper">
                      <label class="input-label">Auth Realm</label>
                      <v-text-field
                        v-model="carrierSettings.realm"
                        placeholder="Leave empty to use SIP Host"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20"
                            >mdi-shield-key-outline</v-icon
                          >
                        </template>
                      </v-text-field>
                      <div class="input-hint">
                        Authentication realm (defaults to SIP Host if empty)
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="input-wrapper">
                      <label class="input-label">From User</label>
                      <v-text-field
                        v-model="carrierSettings.fromUser"
                        placeholder="Leave empty to use Username"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20"
                            >mdi-account-arrow-right</v-icon
                          >
                        </template>
                      </v-text-field>
                      <div class="input-hint">
                        Override the user in SIP From header (defaults to
                        Username)
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div
                      class="input-wrapper d-flex align-center"
                      style="min-height: 48px;"
                    >
                      <v-switch
                        v-model="carrierSettings.register"
                        label="Send Registration"
                        inset
                        dense
                        hide-details
                        color="primary"
                        class="mt-0"
                      ></v-switch>
                    </div>
                    <div class="input-hint">
                      Send SIP REGISTER to the carrier (disable for IP-auth only
                      carriers)
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div
                      class="input-wrapper d-flex align-center"
                      style="min-height: 48px;"
                    >
                      <v-switch
                        v-model="carrierSettings.callerIdInFrom"
                        label="Caller ID in From Header"
                        inset
                        dense
                        hide-details
                        color="primary"
                        class="mt-0"
                      ></v-switch>
                    </div>
                    <div class="input-hint">
                      Place caller ID number in the SIP From header instead of
                      PAI/RPID
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div
                      class="input-wrapper d-flex align-center"
                      style="min-height: 48px;"
                    >
                      <v-switch
                        v-model="carrierSettings.srtpEnabled"
                        label="Enable SRTP (Secure RTP)"
                        inset
                        dense
                        hide-details
                        color="primary"
                        class="mt-0"
                      ></v-switch>
                    </div>
                    <div class="input-hint">
                      Encrypt media with SRTP. Enable only if your carrier
                      requires it — most PSTN carriers do not.
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="input-wrapper">
                      <label class="input-label">From Display Name</label>
                      <v-select
                        v-model="carrierSettings.callerIdNameMode"
                        :items="callerIdNameModeOptions"
                        item-text="label"
                        item-value="value"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20"
                            >mdi-card-account-details-outline</v-icon
                          >
                        </template>
                      </v-select>
                      <div class="input-hint">
                        Controls the display name in the SIP From header sent to
                        the carrier
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    v-if="carrierSettings.callerIdNameMode === 'custom'"
                    cols="12"
                    md="6"
                  >
                    <div class="input-wrapper">
                      <label class="input-label">Custom Display Name</label>
                      <v-text-field
                        v-model="carrierSettings.customCallerIdName"
                        placeholder="e.g. My Company"
                        outlined
                        dense
                        hide-details="auto"
                        class="modern-input"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="20"
                            >mdi-form-textbox</v-icon
                          >
                        </template>
                      </v-text-field>
                      <div class="input-hint">
                        Custom string to send as the From display name
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </div>
        </v-card-text>

        <!-- Footer Actions -->
        <v-divider></v-divider>
        <v-card-actions class="carrier-dialog-footer pa-6">
          <v-btn
            text
            large
            class="px-6"
            :disabled="loader"
            @click="carrierDialog = false"
          >
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            large
            depressed
            class="px-8 save-btn"
            :loading="loader"
            :disabled="loader"
            @click="saveCarrierSettings"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Save Configuration
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DID Management Dialog -->
    <v-dialog
      v-model="didDialog"
      persistent
      max-width="1100"
      scrollable
      transition="dialog-transition"
    >
      <v-card class="did-dialog-card" elevation="24">
        <!-- Modern Header -->
        <div class="did-dialog-header">
          <div class="header-content">
            <div class="header-icon-wrapper">
              <v-icon color="white" size="28">mdi-phone-plus</v-icon>
            </div>
            <div class="header-text">
              <h2 class="dialog-title">DID Number Management</h2>
              <p class="dialog-subtitle">
                Configure incoming phone numbers and routing
              </p>
            </div>
          </div>
          <v-btn icon dark class="close-btn" @click="didDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="did-dialog-content pa-0">
          <!-- Add/Edit Form Section - Hidden when DID exists and not editing -->
          <div
            v-if="newDID.id || existingDIDs.length === 0"
            class="form-container pa-8"
          >
            <div class="form-header mb-6">
              <div class="d-flex align-center">
                <v-icon
                  :color="newDID.id ? 'orange' : 'success'"
                  class="mr-3"
                  size="24"
                >
                  {{ newDID.id ? "mdi-pencil-circle" : "mdi-plus-circle" }}
                </v-icon>
                <h3 class="form-title">
                  {{ newDID.id ? "Edit DID Number" : "Add New DID Number" }}
                </h3>
              </div>
            </div>

            <!-- DID Number Fields -->
            <v-row>
              <v-col cols="12" sm="4">
                <div class="input-wrapper">
                  <label class="input-label">Country Code</label>
                  <v-text-field
                    v-model="newDID.countryCode"
                    placeholder="+1"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-earth</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12" sm="8">
                <div class="input-wrapper">
                  <label class="input-label"
                    >Phone Number <span class="required">*</span></label
                  >
                  <v-text-field
                    v-model="newDID.numberLocal"
                    placeholder="Enter phone number"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-phone</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12" sm="8">
                <div class="input-wrapper">
                  <label class="input-label">Description</label>
                  <v-text-field
                    v-model="newDID.description"
                    placeholder="e.g., Support Line, Sales Line"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-text</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12" sm="4">
                <div class="input-wrapper">
                  <label class="input-label">Carrier</label>
                  <v-select
                    v-model="newDID.carrier"
                    :items="carriers"
                    item-text="name"
                    item-value="id"
                    placeholder="Select carrier"
                    outlined
                    dense
                    hide-details="auto"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20"
                        >mdi-server-network</v-icon
                      >
                    </template>
                  </v-select>
                </div>
              </v-col>
            </v-row>

            <!-- Routing Configuration Section -->
            <div class="routing-section mt-8 mb-6">
              <div class="section-header mb-4">
                <v-icon color="primary" class="mr-2">mdi-phone-forward</v-icon>
                <span class="section-title">Incoming Call Routing</span>
              </div>
              <p class="section-description mb-4">
                Configure how incoming calls should be distributed to agents
              </p>

              <v-row>
                <v-col cols="12" md="6">
                  <div class="input-wrapper">
                    <label class="input-label"
                      >Routing Strategy <span class="required">*</span></label
                    >
                    <v-select
                      v-model="newDID.routingStrategy"
                      :items="routingStrategies"
                      item-text="label"
                      item-value="value"
                      outlined
                      dense
                      hide-details="auto"
                      class="modern-input"
                    >
                      <template v-slot:item="{ item }">
                        <v-icon class="mr-2" small>{{ item.icon }}</v-icon>
                        <span>{{ item.label }}</span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-icon class="mr-2" small>{{ item.icon }}</v-icon>
                        <span>{{ item.label }}</span>
                      </template>
                    </v-select>
                    <div class="input-hint">
                      How should calls be distributed to agents?
                    </div>
                  </div>
                </v-col>

                <!-- Agent Selection (for "agent" strategy) -->
                <v-col
                  v-if="newDID.routingStrategy === 'agent'"
                  cols="12"
                  md="6"
                >
                  <div class="input-wrapper">
                    <label class="input-label"
                      >Select Agent <span class="required">*</span></label
                    >
                    <v-select
                      v-model="newDID.routingAgentUsername"
                      :items="companyAgents"
                      item-text="name"
                      item-value="username"
                      outlined
                      dense
                      hide-details="auto"
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20">mdi-account</v-icon>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon small class="mr-2">mdi-account</v-icon>
                        <span>{{ item.name }} ({{ item.username }})</span>
                      </template>
                    </v-select>
                    <div class="input-hint">
                      This agent will receive all calls
                    </div>
                  </div>
                </v-col>

                <!-- Multiple Agents Selection -->
                <v-col
                  v-if="newDID.routingStrategy === 'random-agent'"
                  cols="12"
                >
                  <div class="input-wrapper">
                    <label class="input-label"
                      >Select Agents <span class="required">*</span></label
                    >
                    <v-select
                      v-model="newDID.routingAgentIds"
                      :items="companyAgents"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      hide-details="auto"
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20"
                          >mdi-account-multiple</v-icon
                        >
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          small
                          close
                          @click:close="
                            newDID.routingAgentIds.splice(
                              newDID.routingAgentIds.indexOf(item.id),
                              1,
                            )
                          "
                        >
                          <v-icon x-small left>mdi-account</v-icon>
                          {{ item.name }}
                        </v-chip>
                        <span v-if="index === 3" class="grey--text caption">
                          (+{{ newDID.routingAgentIds.length - 3 }} more)
                        </span>
                      </template>
                    </v-select>
                    <div class="input-hint">
                      Random agent from selection receives each call
                    </div>
                  </div>

                  <v-checkbox
                    v-if="
                      !newDID.routingAgentIds ||
                        newDID.routingAgentIds.length === 0
                    "
                    v-model="newDID.routingAllAgents"
                    label="Route to all company agents"
                    dense
                    hide-details
                    class="mt-2"
                  ></v-checkbox>
                </v-col>

                <!-- Fallback Strategy -->
                <v-col
                  v-if="newDID.routingStrategy !== 'unassigned'"
                  cols="12"
                  md="6"
                >
                  <div class="input-wrapper">
                    <label class="input-label"
                      >Fallback Strategy
                      <span class="optional">(Optional)</span></label
                    >
                    <v-select
                      v-model="newDID.routingFallback"
                      :items="routingStrategies"
                      item-text="label"
                      item-value="value"
                      outlined
                      dense
                      clearable
                      hide-details="auto"
                      class="modern-input"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="primary" size="20"
                          >mdi-backup-restore</v-icon
                        >
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon class="mr-2" small>{{ item.icon }}</v-icon>
                        <span>{{ item.label }}</span>
                      </template>
                    </v-select>
                    <div class="input-hint">
                      What to do when primary routing fails
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Routing Summary Card -->
              <v-card
                v-if="
                  newDID.routingStrategy &&
                    newDID.routingStrategy !== 'unassigned'
                "
                color="#e3f2fd"
                flat
                class="mt-4 routing-summary-card"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-start">
                    <v-icon color="primary" class="mr-3"
                      >mdi-information-outline</v-icon
                    >
                    <div class="flex-grow-1">
                      <div
                        class="font-weight-bold mb-1"
                        style="color: #1976d2;"
                      >
                        Routing Configuration
                      </div>
                      <div class="text-body-2">
                        <span
                          v-if="
                            newDID.routingStrategy === 'agent' &&
                              newDID.routingAgentUsername
                          "
                        >
                          📞 All calls will be routed to:
                          <strong>{{
                            getAgentName(newDID.routingAgentUsername)
                          }}</strong>
                        </span>
                        <span
                          v-else-if="newDID.routingStrategy === 'random-agent'"
                        >
                          <span v-if="newDID.routingAllAgents">
                            🎲 Random company agent will receive each call
                          </span>
                          <span
                            v-else-if="
                              newDID.routingAgentIds &&
                                newDID.routingAgentIds.length
                            "
                          >
                            🎲 Random selection from
                            <strong>{{ newDID.routingAgentIds.length }}</strong>
                            selected agent(s)
                          </span>
                          <span v-else class="error--text"
                            >⚠️ No agents selected yet</span
                          >
                        </span>
                        <span v-if="newDID.routingFallback">
                          <br />↪️ Fallback:
                          <strong>{{
                            getRoutingStrategyLabel(newDID.routingFallback)
                          }}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions mt-6">
              <v-btn
                text
                color="grey darken-1"
                class="mr-3"
                @click="resetDIDForm"
              >
                <v-icon left>mdi-refresh</v-icon>
                Clear Form
              </v-btn>
              <v-btn
                :color="newDID.id ? 'orange' : 'success'"
                large
                depressed
                class="px-8"
                @click="updateOrAddDID"
              >
                <v-icon left>{{
                  newDID.id ? "mdi-check-circle" : "mdi-plus-circle"
                }}</v-icon>
                {{ newDID.id ? "Update DID" : "Add DID" }}
              </v-btn>
            </div>
          </div>

          <!-- Existing DIDs Table -->
          <v-divider></v-divider>

          <div class="pa-8 pt-6">
            <div class="d-flex align-center mb-4">
              <v-icon color="primary" class="mr-2"
                >mdi-format-list-bulleted-square</v-icon
              >
              <h3 class="table-title">Configured DID Numbers</h3>
              <v-spacer></v-spacer>
              <v-chip small color="primary" outlined>
                {{ existingDIDs.length }} DID{{
                  existingDIDs.length !== 1 ? "s" : ""
                }}
              </v-chip>
            </div>

            <v-data-table
              :headers="didHeaders"
              :items="existingDIDs"
              :items-per-page="5"
              class="did-table elevation-0"
            >
              <template v-slot:item.number="{ item }">
                <div class="d-flex align-center">
                  <div class="number-badge mr-2">
                    <v-icon x-small color="white">mdi-phone</v-icon>
                  </div>
                  <strong class="did-number-text">{{ item.number }}</strong>
                </div>
              </template>
              <template v-slot:item.routing="{ item }">
                <v-chip
                  v-if="item.routing && item.routing.type"
                  small
                  :color="getRoutingColor(item.routing.type)"
                  text-color="white"
                  class="routing-chip"
                >
                  <v-icon x-small left>{{
                    getRoutingIcon(item.routing.type)
                  }}</v-icon>
                  {{ getRoutingStrategyLabel(item.routing.type) }}
                </v-chip>
                <v-chip v-else small color="grey lighten-2">
                  <v-icon x-small left>mdi-help-circle</v-icon>
                  Not Set
                </v-chip>
              </template>
              <template v-slot:item.routingDetails="{ item }">
                <div class="d-flex align-center">
                  <span
                    v-if="
                      item.routing &&
                        item.routing.type === 'agent' &&
                        item.routing.agentUsername
                    "
                  >
                    <v-icon x-small class="mr-1" color="primary"
                      >mdi-account</v-icon
                    >
                    {{ item.routing.agentUsername }}
                  </span>
                  <span
                    v-else-if="
                      item.routing && item.routing.type === 'random-agent'
                    "
                  >
                    <v-icon x-small class="mr-1" color="primary"
                      >mdi-account-multiple</v-icon
                    >
                    <span
                      v-if="
                        item.routing.agentIds && item.routing.agentIds.length
                      "
                    >
                      {{ item.routing.agentIds.length }} agent(s)
                    </span>
                    <span v-else>All agents</span>
                  </span>
                  <span v-else class="grey--text">-</span>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="mr-1"
                      @click="editDID(item)"
                      v-on="on"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit DID</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="deleteDID(item)"
                      v-on="on"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete DID</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Loader -->
    <v-dialog v-model="loader" persistent width="300">
      <v-card color="primary white--text" dark>
        <v-card-text>
          Please stand by
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Preloader -->
    <v-card-text v-if="preloader" style="padding: 50px">
      <div style="text-align:center;" align="center">
        <v-progress-circular
          indeterminate
          color="green"
          :size="38"
        ></v-progress-circular>
      </div>
    </v-card-text>

    <!-- Twilio Auth Dialog -->
    <TwilioAuthDialog
      v-model="showTwilioAuthDialog"
      @complete="handleTwilioComplete"
      @close="showTwilioAuthDialog = false"
    />

    <!-- PBX Diagnostics Dialog -->
    <v-dialog v-model="diagnosticsDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-bug</v-icon>
          PBX Diagnostics & Settings
          <v-spacer></v-spacer>
          <v-btn icon dark @click="diagnosticsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Custom Settings Section -->
          <v-card outlined class="mb-4">
            <v-card-title
              class="subtitle-1 font-weight-bold d-flex align-center"
            >
              <v-icon small class="mr-2">mdi-tune</v-icon>
              SIP Connection Settings
              <v-spacer></v-spacer>
              <v-chip
                small
                :color="useCustomSettings ? 'orange' : 'success'"
                text-color="white"
              >
                {{ useCustomSettings ? "Custom" : "Auto" }}
              </v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-switch
                v-model="editCustomSettings"
                label="Use custom SIP settings (override auto-configuration)"
                color="primary"
                dense
                hide-details
                class="mb-4"
              ></v-switch>

              <v-expand-transition>
                <div v-if="editCustomSettings">
                  <!-- Show preset options for dev/staging environments -->
                  <div v-if="showSipPresets" class="mb-4">
                    <div class="subtitle-2 mb-2 grey--text text--darken-1">
                      <v-icon small class="mr-1">mdi-lightning-bolt</v-icon>
                      Quick Presets (Dev/Staging)
                    </div>
                    <v-radio-group
                      v-model="selectedSipPreset"
                      row
                      dense
                      hide-details
                      class="mt-0 mb-3"
                      @change="applySipPreset"
                    >
                      <v-radio
                        v-for="preset in sipPresets"
                        :key="preset.value"
                        :label="preset.label"
                        :value="preset.value"
                        class="mr-4"
                      ></v-radio>
                    </v-radio-group>
                    <v-divider class="mb-4"></v-divider>
                  </div>

                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="customSipDomain"
                        label="SIP Domain / Server"
                        placeholder="127.0.0.1 or sip.godial.cc"
                        outlined
                        dense
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small color="grey">mdi-server</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="customWebsocket"
                        label="WebSocket URL"
                        placeholder="ws://127.0.0.1:5066 or wss://sip.godial.cc/ws/"
                        outlined
                        dense
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small color="grey">mdi-web</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="customIceServers"
                        label="ICE Servers (comma-separated)"
                        placeholder="stun:stun.l.google.com:19302,turn:turn.example.com:3478"
                        outlined
                        dense
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon small color="grey">mdi-router-network</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <div class="d-flex mt-3">
                    <v-btn small text color="grey" @click="resetDialerSettings">
                      <v-icon small left>mdi-refresh</v-icon>
                      Reset to Auto
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      small
                      color="primary"
                      depressed
                      @click="saveDialerSettings"
                    >
                      <v-icon small left>mdi-content-save</v-icon>
                      Save Settings
                    </v-btn>
                  </div>
                </div>
              </v-expand-transition>

              <!-- Show current effective config when not editing -->
              <div v-if="!editCustomSettings" class="mt-2">
                <v-alert type="info" text dense class="mb-2">
                  <v-icon left small>mdi-shield-check</v-icon>
                  Settings are auto-configured based on environment. Toggle
                  above to customize.
                </v-alert>
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-medium" width="150">
                        Environment
                      </td>
                      <td>
                        <v-chip
                          x-small
                          :color="
                            sipEnvironment === 'development'
                              ? 'orange'
                              : 'success'
                          "
                          text-color="white"
                        >
                          {{
                            sipEnvironment === "development"
                              ? "Development"
                              : "Production"
                          }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">SIP Domain</td>
                      <td>
                        <code>{{ sipConfig.domain }}</code>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">WebSocket</td>
                      <td>
                        <code>{{ sipConfig.wsServers }}</code>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-medium">ICE Servers</td>
                      <td>
                        <code class="text-caption">{{
                          sipConfig.iceServers
                        }}</code>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-card-text>
          </v-card>

          <!-- Authentication Section -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon small class="mr-2">mdi-account-key</v-icon>
              Authentication (Auto from Login)
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="150">User ID</td>
                    <td>
                      <code>{{
                        currentUser.id || currentUser.userId || "N/A"
                      }}</code>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Username</td>
                    <td>
                      <code>{{
                        currentUser.username || currentUser.email || "N/A"
                      }}</code>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Auth Token</td>
                    <td>
                      <v-chip
                        small
                        :color="currentUser.token ? 'success' : 'error'"
                        text-color="white"
                      >
                        {{
                          currentUser.token
                            ? "Present (" +
                              currentUser.token.substring(0, 8) +
                              "...)"
                            : "Missing"
                        }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Status</td>
                    <td>
                      <v-chip
                        small
                        :color="
                          dialerConnectionStatus === 'registered'
                            ? 'success'
                            : dialerConnectionStatus === 'error'
                            ? 'error'
                            : 'warning'
                        "
                        text-color="white"
                      >
                        {{ dialerConnectionText }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- Debug Logs Section -->
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
              Debug Logs
              <v-spacer></v-spacer>
              <v-btn x-small text color="primary" @click="copyDiagnosticLogs">
                <v-icon x-small left>mdi-content-copy</v-icon>
                Copy
              </v-btn>
              <v-btn x-small text color="error" @click="clearDiagnosticLogs">
                <v-icon x-small left>mdi-delete</v-icon>
                Clear
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="max-height: 250px; overflow-y: auto;">
              <div v-if="debugLogs && debugLogs.length > 0">
                <div
                  v-for="(log, index) in debugLogs"
                  :key="index"
                  class="log-entry mb-2 pa-2"
                  :class="'log-' + log.level"
                >
                  <div class="d-flex align-center">
                    <v-icon
                      x-small
                      class="mr-1"
                      :color="
                        log.level === 'error'
                          ? 'error'
                          : log.level === 'warning'
                          ? 'orange'
                          : log.level === 'success'
                          ? 'success'
                          : 'info'
                      "
                    >
                      {{
                        log.level === "error"
                          ? "mdi-alert-circle"
                          : log.level === "warning"
                          ? "mdi-alert"
                          : log.level === "success"
                          ? "mdi-check-circle"
                          : "mdi-information"
                      }}
                    </v-icon>
                    <span class="text-caption grey--text mr-2">{{
                      log.timestamp
                    }}</span>
                    <span class="text-body-2">{{ log.message }}</span>
                  </div>
                  <div
                    v-if="log.details"
                    class="text-caption grey--text mt-1 pl-5"
                  >
                    <code>{{ JSON.stringify(log.details) }}</code>
                  </div>
                </div>
              </div>
              <div v-else class="text-center grey--text pa-4">
                <v-icon large color="grey lighten-2"
                  >mdi-text-box-outline</v-icon
                >
                <p class="mt-2 mb-0">
                  No debug logs yet. Try connecting to the SIP server.
                </p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Connection Error Alert in Diagnostics -->
          <v-alert v-if="connectionError" type="error" dense class="mt-4 mb-0">
            <div class="d-flex align-center">
              <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
              <div><strong>Last Error:</strong> {{ connectionError }}</div>
            </div>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn text @click="diagnosticsDialog = false">Close</v-btn>
          <v-spacer></v-spacer>

          <!-- Cancel Button when connecting -->
          <v-btn
            v-if="dialerConnectionStatus === 'connecting'"
            color="error"
            outlined
            class="mr-2"
            @click="cancelDialerConnection"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            depressed
            :disabled="dialerConnectionStatus === 'registered'"
            :loading="dialerConnectionStatus === 'connecting'"
            @click="connectDialer"
          >
            <v-icon left small>mdi-connection</v-icon>
            {{
              dialerConnectionStatus === "registered"
                ? "Connected"
                : dialerConnectionStatus === "error"
                ? "Retry Connection"
                : "Test Connection"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Carrier Connection Test Dialog -->
    <CarrierConnectionTestDialog
      v-model="showConnectionTestDialog"
      :company-id="companyId"
    />

    <!-- PayWall Card -->
    <PayWallCard
      title="Unlock GoDial PBX"
      description="Get enterprise-grade cloud telephony with your own SIP carrier. Your current plan is SIM-based only."
      badge="PBX Subscription Required"
      badge-icon="mdi-phone-voip"
      badge-color="info"
      :features="pbxFeatures"
      button-text="Upgrade to PBX Plan"
      :force-show="isPbxLocked"
      @update-is-expired="handlePaywallUpdate"
    />
  </v-container>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import TwilioAuthDialog from "@/components/TwilioAuthDialog.vue";
import CarrierConnectionTestDialog from "@/components/CarrierConnectionTestDialog.vue";
import PayWallCard from "@/components/payWallCard.vue";

export default {
  name: "PBX",
  components: {
    TwilioAuthDialog,
    CarrierConnectionTestDialog,
    PayWallCard,
  },
  data: () => ({
    showHeader: false,
    preloader: false,
    loader: false,
    isExpired: false,
    pbxId: null, // Store PBX config ID for updates
    pbxFeatures: [
      "Connect your own SIP carrier",
      "Web-based softphone dialer",
      "Inbound & outbound calling",
      "Call recordings & analytics",
    ],
    carrierDialog: false,
    didDialog: false,
    showTwilioAuthDialog: false, // Twilio auth dialog
    showConnectionTestDialog: false, // Carrier connection test dialog
    diagnosticsDialog: false, // PBX diagnostics dialog
    carrierSettings: {
      name: "",
      host: "",
      port: "",
      username: "",
      password: "",
      fromDomain: "",
      prefix: "",
      transport: "udp",
      realm: "",
      fromUser: "",
      register: true,
      callerIdInFrom: false,
      srtpEnabled: false,
      callerIdNameMode: "agent",
      customCallerIdName: "",
    },
    callerIdNameModeOptions: [
      { value: "agent", label: "Agent Name (default)" },
      { value: "did", label: "DID Number" },
      { value: "custom", label: "Custom Value" },
    ],
    showAdvancedSettings: false,
    newDID: {
      countryCode: "+1",
      numberLocal: "",
      description: "",
      carrier: "",
      routingStrategy: "random-agent",
      routingAgentUsername: "",
      routingAgentIds: [],
      routingAllAgents: true,
      routingFallback: "",
    },
    carriers: [],
    existingDIDs: [],
    companyAgents: [],
    routingStrategies: [
      { value: "agent", label: "Specific Agent", icon: "mdi-account" },
      {
        value: "random-agent",
        label: "Random Agent",
        icon: "mdi-shuffle-variant",
      },
      {
        value: "unassigned",
        label: "Unassigned (Reject)",
        icon: "mdi-close-circle",
      },
    ],
    didHeaders: [
      { text: "DID Number", value: "number", width: "15%" },
      { text: "Description", value: "description", width: "20%" },
      { text: "Routing", value: "routing", width: "15%" },
      { text: "Route To", value: "routingDetails", width: "20%" },
      { text: "Carrier", value: "carrier", width: "15%" },
      { text: "Actions", value: "actions", sortable: false, width: "15%" },
    ],
    pbxSettings: {
      carrier: {
        name: "",
        host: "",
        port: "",
        username: "",
        password: "",
        fromDomain: "",
        enabled: false,
      },
      dids: [],
      defaultCallingListId: null,
    },
    carrierEnabledLocal: false,
    companyLists: [],
    defaultCallingListId: null,
    // Custom dialer settings
    editCustomSettings: false,
    customSipDomain: "",
    customWebsocket: "",
    customIceServers: "",
    // SIP server presets for dev/staging
    selectedSipPreset: null,
    sipPresets: [
      {
        value: "local",
        label: "Local (127.0.0.1)",
        domain: "127.0.0.1",
        wsServers: "ws://127.0.0.1:5066",
        iceServers: "stun:stun.l.google.com:19302",
      },
      {
        value: "production",
        label: "Production (sip.godial.cc)",
        domain: "sip.godial.cc",
        wsServers: "wss://sip.godial.cc/ws/",
        iceServers: "stun:stun.l.google.com:19302",
      },
      {
        value: "staging",
        label: "Staging (staging-sip.godial.cc)",
        domain: "staging-sip.godial.cc",
        wsServers: "wss://staging-sip.godial.cc/ws/",
        iceServers: "stun:stun.l.google.com:19302",
      },
    ],
  }),
  computed: {
    ...mapState("dialer", [
      "connectionStatus",
      "connectionError",
      "connectionProgress",
      "isInCall",
      "callDuration",
      "currentNumber",
      "settings",
    ]),
    ...mapGetters("dialer", [
      "isConnected",
      "connectionText",
      "sipConfig",
      "sipEnvironment",
      "debugLogs",
      "autoSipConfig",
      "useCustomSettings",
      "customSettings",
    ]),
    isPbxLocked() {
      return this.$store.getters.isPbxLocked;
    },
    companyId() {
      return this.$store.getters.user
        ? this.$store.getters.user.companyId
        : null;
    },
    currentUser() {
      return this.$store.state.user || {};
    },
    isCarrierConfigured() {
      const c = this.carrierSettings;
      return !!(c && (c.name || c.host || c.username));
    },
    dialerConnectionStatus() {
      return this.connectionStatus;
    },
    dialerConnectionText() {
      return this.connectionText;
    },
    dialerSettings() {
      return this.settings;
    },
    // Dialer is always "configured" now since it auto-configures from environment
    isDialerConfigured() {
      return !!(this.currentUser && this.currentUser.token);
    },
    // Show SIP presets only for localhost or staging environments
    showSipPresets() {
      const endpoint = process.env.VUE_APP_ENDPOINT || "";
      const isLocalhost =
        endpoint.includes("localhost") || endpoint.includes("127.0.0.1");
      const isStaging = endpoint.includes("staging");
      return isLocalhost || isStaging;
    },
  },
  async created() {
    // Check if header was previously dismissed
    if (localStorage.getItem("pbxHeaderDismissed") === "true") {
      this.showHeader = false;
    }
    await this.loadPBXSettings();
    await this.loadCompanyAgents();
    await this.loadCompanyLists();
    await this.$store.dispatch("dialer/loadSettings");
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("pbxHeaderDismissed", "true");
    },
    handlePaywallUpdate(isExpired) {
      this.isExpired = isExpired;
    },
    async connectDialer() {
      try {
        await this.$store.dispatch("dialer/connect");
        this.$swal({
          type: "success",
          title: "Connected!",
          text: "Web dialer is now ready to make calls",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to connect dialer:", error);
        // Error is already set in store, show detailed message
        this.$swal({
          type: "error",
          title: "Connection Failed",
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 12px;"><strong>Error:</strong> ${error.message ||
                "Failed to connect to SIP server"}</p>
              <p style="margin-bottom: 8px;"><strong>Common fixes:</strong></p>
              <ol style="padding-left: 20px; margin: 0; line-height: 1.8;">
                <li>Check your internet connection</li>
                <li>Verify the SIP server address is correct</li>
                <li>Make sure the WebSocket URL uses the right protocol (ws/wss)</li>
                <li>Try resetting to auto-detected settings</li>
              </ol>
            </div>
          `,
          confirmButtonText: "Open Settings",
          showCancelButton: true,
          cancelButtonText: "Close",
        }).then((result) => {
          if (result.value) {
            this.openDiagnosticsDialog();
          }
        });
      }
    },

    cancelDialerConnection() {
      this.$store.dispatch("dialer/cancelConnection");
      this.$swal({
        type: "info",
        title: "Connection Cancelled",
        text: "The connection attempt was cancelled",
        timer: 1500,
        showConfirmButton: false,
      });
    },

    openDiagnosticsDialog() {
      // Load current custom settings into form
      this.editCustomSettings = this.useCustomSettings;
      if (this.customSettings) {
        this.customSipDomain = this.customSettings.domain || "";
        this.customWebsocket = this.customSettings.wsServers || "";
        this.customIceServers = this.customSettings.iceServers || "";
      } else {
        // Pre-fill with auto-detected values
        const auto = this.autoSipConfig;
        this.customSipDomain = auto.domain || "";
        this.customWebsocket = auto.wsServers || "";
        this.customIceServers = auto.iceServers || "";
      }
      this.diagnosticsDialog = true;
    },

    async saveDialerSettings() {
      try {
        if (this.editCustomSettings) {
          await this.$store.dispatch("dialer/saveCustomSettings", {
            domain: this.customSipDomain,
            wsServers: this.customWebsocket,
            iceServers: this.customIceServers,
          });
          this.$swal({
            type: "success",
            title: "Settings Saved",
            text:
              "Custom SIP settings have been saved. Click Connect to apply.",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          await this.$store.dispatch("dialer/resetToAutoSettings");
          this.$swal({
            type: "success",
            title: "Settings Reset",
            text: "Using auto-detected settings based on environment.",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error("Failed to save settings:", error);
        this.$swal({
          type: "error",
          title: "Save Failed",
          text: error.message || "Failed to save settings",
        });
      }
    },

    async resetDialerSettings() {
      await this.$store.dispatch("dialer/resetToAutoSettings");
      // Reset form to auto values
      const auto = this.autoSipConfig;
      this.customSipDomain = auto.domain || "";
      this.customWebsocket = auto.wsServers || "";
      this.customIceServers = auto.iceServers || "";
      this.editCustomSettings = false;
      this.selectedSipPreset = null;
      this.$swal({
        type: "info",
        title: "Reset Complete",
        text: "Settings reset to auto-detected values.",
        timer: 1500,
        showConfirmButton: false,
      });
    },

    // Apply selected SIP preset to form fields
    applySipPreset(presetValue) {
      const preset = this.sipPresets.find((p) => p.value === presetValue);
      if (preset) {
        this.customSipDomain = preset.domain;
        this.customWebsocket = preset.wsServers;
        this.customIceServers = preset.iceServers;
      }
    },

    async loadPBXSettings() {
      if (!this.companyId) return;

      try {
        this.preloader = true;

        // Query PBX model directly using companyId filter
        const response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/pbx`,
          {
            params: {
              filter: JSON.stringify({ where: { companyId: this.companyId } }),
            },
          },
        );

        const pbxConfigs = response.body;
        const pbxConfig =
          pbxConfigs && pbxConfigs.length > 0 ? pbxConfigs[0] : null;

        if (pbxConfig) {
          // Store the PBX ID for future updates
          this.pbxId = pbxConfig.id;
          this.pbxSettings = {
            carrier: pbxConfig.carrier || {},
            dids: pbxConfig.dids || [],
            defaultCallingListId: pbxConfig.defaultCallingListId || null,
          };

          // Map carrier settings, handling Twilio-specific fields
          const carrier = this.pbxSettings.carrier;
          this.carrierSettings = { ...carrier };

          // For Twilio Elastic SIP Trunks, use credentialUsername/credentialPassword for SIP auth
          if (
            carrier.type === "twilio-elastic-trunk" ||
            carrier.type === "twilio-connect"
          ) {
            // Use credential fields if available, otherwise fall back to username/password
            if (carrier.credentialUsername) {
              this.carrierSettings.username = carrier.credentialUsername;
            }
            if (carrier.credentialPassword) {
              this.carrierSettings.password = carrier.credentialPassword;
            }
          }

          this.existingDIDs = Array.isArray(this.pbxSettings.dids)
            ? [...this.pbxSettings.dids]
            : [];
          this.carrierEnabledLocal = !!this.carrierSettings.enabled;
          this.defaultCallingListId = this.pbxSettings.defaultCallingListId;

          // Load carriers list
          this.carriers = [];
          if (
            this.carrierSettings &&
            (this.carrierSettings.name || this.carrierSettings.host)
          ) {
            this.carriers.push({
              id: "default",
              name: this.carrierSettings.name || this.carrierSettings.host,
            });
            if (!this.newDID.carrier) this.newDID.carrier = "default";
          }
        } else {
          // No PBX config exists yet - will be created on first save
          this.pbxId = null;
          this.pbxSettings = {
            carrier: {
              name: "",
              host: "",
              port: "",
              username: "",
              password: "",
              fromDomain: "",
              enabled: false,
              prefix: "",
              transport: "udp",
              realm: "",
              fromUser: "",
              register: true,
              callerIdInFrom: false,
              srtpEnabled: false,
            },
            dids: [],
            defaultCallingListId: null,
          };
          this.carrierSettings = { ...this.pbxSettings.carrier };
          this.existingDIDs = [];
          this.carrierEnabledLocal = false;
          this.carriers = [];
          this.defaultCallingListId = null;
        }
      } catch (error) {
        console.error("Error loading PBX settings:", error);
        this.$swal({
          type: "error",
          text: "Failed to load PBX settings",
        });
      } finally {
        this.preloader = false;
      }
    },

    async savePBXConfig(data) {
      // Helper method to create or update PBX config
      if (this.pbxId) {
        // Update existing
        const response = await this.$http.patch(
          `${this.$store.state.ENDPOINT}/pbx/${this.pbxId}`,
          data,
        );
        return response.body;
      } else {
        // Create new
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/pbx`,
          { ...data, companyId: this.companyId },
        );
        this.pbxId = response.body.id;
        return response.body;
      }
    },

    async loadCompanyAgents() {
      if (!this.companyId) return;

      try {
        const response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/accounts`,
          {
            params: {
              filter: JSON.stringify({
                where: {
                  companyId: this.companyId,
                  disabled: { neq: true },
                },
                fields: {
                  id: true,
                  name: true,
                  username: true,
                  email: true,
                },
              }),
            },
          },
        );

        this.companyAgents = response.body || [];
      } catch (error) {
        console.error("Error loading company agents:", error);
        this.companyAgents = [];
      }
    },

    async loadCompanyLists() {
      if (!this.companyId) return;

      try {
        const response = await this.$http.get(
          `${this.$store.state.ENDPOINT}/lists`,
          {
            params: {
              filter: JSON.stringify({
                where: { companyId: this.companyId },
                order: "createdOn DESC",
                fields: {
                  id: true,
                  name: true,
                },
              }),
            },
          },
        );

        this.companyLists = response.body || [];
      } catch (error) {
        console.error("Error loading company lists:", error);
        this.companyLists = [];
      }
    },

    async saveDefaultCallingList() {
      if (!this.defaultCallingListId) {
        this.$swal({
          type: "warning",
          text: "Please select a calling list first",
        });
        return;
      }

      try {
        this.loader = true;

        await this.savePBXConfig({
          defaultCallingListId: this.defaultCallingListId,
        });

        this.$swal({
          type: "success",
          text: "Default calling list saved successfully!",
          timer: 2000,
        });

        // Update local state
        this.pbxSettings.defaultCallingListId = this.defaultCallingListId;
      } catch (error) {
        console.error("Error saving default calling list:", error);
        this.$swal({
          type: "error",
          text: "Failed to save default calling list",
        });
      } finally {
        this.loader = false;
      }
    },

    openCarrierDialog() {
      // Reset form to current saved settings (from pbxSettings)
      // This ensures we start fresh each time and cancel works properly
      const savedCarrier = this.pbxSettings.carrier || {};
      this.carrierSettings = {
        name: savedCarrier.name || "",
        host: savedCarrier.host || "",
        port: savedCarrier.port || "5060",
        username: savedCarrier.username || "",
        password: savedCarrier.password || "",
        fromDomain: savedCarrier.fromDomain || "",
        enabled: savedCarrier.enabled || false,
        prefix: savedCarrier.prefix || "",
        transport: savedCarrier.transport || "udp",
        realm: savedCarrier.realm || "",
        fromUser: savedCarrier.fromUser || "",
        register:
          savedCarrier.register !== undefined
            ? savedCarrier.register
            : savedCarrier.registrationRequired !== undefined
              ? savedCarrier.registrationRequired
              : true,
        callerIdInFrom: savedCarrier.callerIdInFrom || false,
        srtpEnabled: savedCarrier.srtpEnabled || false,
        callerIdNameMode: savedCarrier.callerIdNameMode || "agent",
        customCallerIdName: savedCarrier.customCallerIdName || "",
      };
      // Auto-expand advanced settings if any advanced field is configured
      this.showAdvancedSettings = !!(
        savedCarrier.prefix ||
        savedCarrier.realm ||
        savedCarrier.fromUser ||
        (savedCarrier.transport && savedCarrier.transport !== "udp") ||
        savedCarrier.register === false ||
        savedCarrier.callerIdInFrom ||
        savedCarrier.srtpEnabled ||
        (savedCarrier.callerIdNameMode &&
          savedCarrier.callerIdNameMode !== "agent")
      );

      // For Twilio, use credential fields
      if (
        savedCarrier.type === "twilio-elastic-trunk" ||
        savedCarrier.type === "twilio-connect"
      ) {
        if (savedCarrier.credentialUsername) {
          this.carrierSettings.username = savedCarrier.credentialUsername;
        }
        if (savedCarrier.credentialPassword) {
          this.carrierSettings.password = savedCarrier.credentialPassword;
        }
      }

      this.carrierDialog = true;
    },
    openDIDDialog() {
      this.resetDIDForm();
      this.didDialog = true;
    },

    resetDIDForm() {
      this.newDID = {
        countryCode: "+1",
        numberLocal: "",
        description: "",
        carrier: this.carriers.length > 0 ? this.carriers[0].id : "",
        routingStrategy: "agent",
        routingAgentUsername: "",
        routingAgentIds: [],
        routingAllAgents: true,
        routingFallback: "",
      };
    },

    async saveCarrierSettings() {
      if (!this.companyId) return;

      // Validate required fields
      const { host, port, username, password } = this.carrierSettings;

      if (!host || host.trim() === "") {
        this.$swal({
          type: "warning",
          title: "Missing Required Field",
          text: "Please enter a SIP Host address",
        });
        return;
      }

      if (!username || username.trim() === "") {
        this.$swal({
          type: "warning",
          title: "Missing Required Field",
          text: "Please enter a username for authentication",
        });
        return;
      }

      if (!password || password.trim() === "") {
        this.$swal({
          type: "warning",
          title: "Missing Required Field",
          text: "Please enter a password for authentication",
        });
        return;
      }

      try {
        this.loader = true;

        // Prepare carrier config for saving
        // Start with the original carrier to preserve server-set fields
        // (type, registrationRequired, accountSid, trunkSid, etc.)
        const originalCarrier = this.pbxSettings.carrier || {};
        const carrierToSave = {
          ...originalCarrier,
          ...this.carrierSettings,
          host: host.trim(),
          port: port || "5060",
          username: username.trim(),
          enabled: true,
        };

        // For Twilio types, map username/password back to credential fields
        const carrierType = originalCarrier.type || null;
        if (
          carrierType === "twilio-elastic-trunk" ||
          carrierType === "twilio-connect"
        ) {
          // Update credential fields with any changes from the form
          carrierToSave.credentialUsername = this.carrierSettings.username;
          carrierToSave.credentialPassword = this.carrierSettings.password;
          // Preserve accountSid in its own field (don't overwrite username)
          if (originalCarrier.accountSid) {
            carrierToSave.accountSid = originalCarrier.accountSid;
          }
          // Preserve trunkSid
          if (originalCarrier.trunkSid) {
            carrierToSave.trunkSid = originalCarrier.trunkSid;
          }
        }

        // Save directly to PBX model
        await this.savePBXConfig({
          carrier: carrierToSave,
          dids: this.existingDIDs,
        });

        // Notify FreeSWITCH to reload the gateway with updated config
        try {
          await this.$http.post(
            `${this.$store.state.ENDPOINT}/pbx/notify-carrier-change`,
            {
              companyId: this.companyId,
              secret: "abc123",
            },
          );
        } catch (syncErr) {
          console.warn("Carrier sync to PBX failed (non-fatal):", syncErr);
        }

        this.carrierDialog = false;
        this.$swal({
          type: "success",
          text: "Carrier settings saved successfully",
        });

        // Reload settings
        await this.loadPBXSettings();
        
        // Refresh PBX data in Vuex store to update dialer visibility
        await this.$store.dispatch('refreshPbxData');
      } catch (error) {
        console.error("Error saving carrier settings:", error);
        this.$swal({
          type: "error",
          text: error.body
            ? error.body.error.message
            : "Failed to save carrier settings",
        });
      } finally {
        this.loader = false;
      }
    },
    async onToggleCarrierEnabled(value) {
      try {
        this.loader = true;

        // Prepare carrier config for saving
        const carrierToSave = { ...this.pbxSettings.carrier, enabled: !!value };

        // For Twilio types, ensure credential fields are preserved
        const carrierType = this.pbxSettings.carrier
          ? this.pbxSettings.carrier.type
          : null;
        if (
          carrierType === "twilio-elastic-trunk" ||
          carrierType === "twilio-connect"
        ) {
          // Preserve all Twilio-specific fields
          carrierToSave.credentialUsername = this.pbxSettings.carrier.credentialUsername;
          carrierToSave.credentialPassword = this.pbxSettings.carrier.credentialPassword;
          carrierToSave.accountSid = this.pbxSettings.carrier.accountSid;
          carrierToSave.trunkSid = this.pbxSettings.carrier.trunkSid;
        }

        // Save directly to PBX model
        await this.savePBXConfig({
          carrier: carrierToSave,
          dids: this.existingDIDs,
        });

        // Notify FreeSWITCH to reload the gateway with updated config
        try {
          await this.$http.post(
            `${this.$store.state.ENDPOINT}/pbx/notify-carrier-change`,
            {
              companyId: this.companyId,
              secret: "abc123",
            },
          );
        } catch (syncErr) {
          console.warn("Carrier sync to PBX failed (non-fatal):", syncErr);
        }

        this.carrierSettings.enabled = !!value;
        
        // Refresh PBX data in Vuex store to update dialer visibility
        await this.$store.dispatch('refreshPbxData');
      } catch (error) {
        this.carrierEnabledLocal = !!this.carrierSettings.enabled; // revert toggle
        this.$swal({ type: "error", text: "Failed to update carrier status" });
      } finally {
        this.loader = false;
      }
    },
    async testCarrierClick() {
      if (!this.companyId) {
        this.$swal({
          type: "warning",
          text: "No company ID found. Please refresh the page.",
        });
        return;
      }
      // Open the comprehensive connection test dialog
      this.showConnectionTestDialog = true;
    },
    async addDID() {
      if (
        !this.newDID.numberLocal ||
        this.newDID.numberLocal.toString().trim() === ""
      ) {
        this.$swal({
          type: "warning",
          text: "Please enter a DID number",
        });
        return;
      }

      // Validate routing configuration
      if (
        this.newDID.routingStrategy === "agent" &&
        !this.newDID.routingAgentUsername
      ) {
        this.$swal({
          type: "warning",
          text: "Please select an agent for routing",
        });
        return;
      }

      if (
        this.newDID.routingStrategy === "random-agent" &&
        !this.newDID.routingAllAgents &&
        (!this.newDID.routingAgentIds ||
          this.newDID.routingAgentIds.length === 0)
      ) {
        this.$swal({
          type: "warning",
          text:
            "Please select agents for routing or check 'Route to all agents'",
        });
        return;
      }

      if (!this.companyId) return;

      try {
        this.loader = true;
        // Build new DID
        const selectedCarrier = this.carriers.find(
          (c) => c.id === this.newDID.carrier,
        );
        const fullNumber = `${(this.newDID.countryCode || "").trim()}${(
          this.newDID.numberLocal || ""
        )
          .toString()
          .trim()}`.replace(/\s+/g, "");

        // Build routing configuration
        const routing = {
          type: this.newDID.routingStrategy,
          strategy: this.newDID.routingStrategy,
        };

        if (this.newDID.routingStrategy === "agent") {
          routing.agentUsername = this.newDID.routingAgentUsername;
        } else if (this.newDID.routingStrategy === "random-agent") {
          if (
            !this.newDID.routingAllAgents &&
            this.newDID.routingAgentIds &&
            this.newDID.routingAgentIds.length
          ) {
            routing.agentIds = this.newDID.routingAgentIds;
          }
          // If routingAllAgents is true, don't set agentIds (will route to all)
        }

        if (
          this.newDID.routingFallback &&
          this.newDID.routingFallback !== this.newDID.routingStrategy
        ) {
          routing.fallback = this.newDID.routingFallback;
        }

        const didToAdd = {
          id: Date.now().toString(),
          number: fullNumber,
          normalizedNumber: fullNumber.replace(/[^0-9+]/g, ""),
          description: this.newDID.description || "",
          carrier: selectedCarrier ? selectedCarrier.name : "",
          enabled: true,
          routing: routing,
          createdAt: new Date().toISOString(),
        };

        // Save directly to PBX model
        await this.savePBXConfig({
          carrier: { ...this.carrierSettings },
          dids: [...this.existingDIDs, didToAdd],
        });

        this.newDID = {
          countryCode: this.newDID.countryCode,
          numberLocal: "",
          description: "",
          carrier: this.newDID.carrier,
          routingStrategy: "agent",
          routingAgentUsername: "",
          routingAgentIds: [],
          routingAllAgents: true,
          routingFallback: "",
        };

        this.$swal({
          type: "success",
          text: "DID added successfully with routing configuration",
        });
        await this.loadPBXSettings();
        
        // Refresh PBX data in Vuex store to update dialer visibility
        await this.$store.dispatch('refreshPbxData');
      } catch (error) {
        console.error("Error adding DID:", error);
        this.$swal({
          type: "error",
          text: error.body ? error.body.error.message : "Failed to add DID",
        });
      } finally {
        this.loader = false;
      }
    },
    editDID(item) {
      // Populate form with existing DID data
      const parts = item.number.match(/^(\+\d+)?(.+)$/);
      this.newDID = {
        id: item.id,
        countryCode: parts && parts[1] ? parts[1] : "+1",
        numberLocal: parts && parts[2] ? parts[2] : item.number,
        description: item.description || "",
        carrier: item.carrier || this.newDID.carrier,
        routingStrategy:
          item.routing && item.routing.type ? item.routing.type : "unassigned",
        routingAgentUsername:
          item.routing && item.routing.agentUsername
            ? item.routing.agentUsername
            : "",
        routingAgentIds:
          item.routing && item.routing.agentIds
            ? [...item.routing.agentIds]
            : [],
        routingAllAgents: item.routing && !item.routing.agentIds,
        routingFallback:
          item.routing && item.routing.fallback ? item.routing.fallback : "",
      };

      this.$swal({
        type: "info",
        title: "Edit DID",
        text: "Update the details below and click 'Update DID' to save changes",
        confirmButtonText: "OK",
      });
    },
    async deleteDID(item) {
      this.$swal({
        type: "warning",
        title: "Are you sure?",
        text: `This will delete DID ${item.number}`,
        showCancelButton: true,
        confirmButtonColor: "#1bca3f",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.value) {
          try {
            this.loader = true;
            const filtered = this.existingDIDs.filter((d) => d.id !== item.id);

            // Save directly to PBX model
            await this.savePBXConfig({
              carrier: { ...this.carrierSettings },
              dids: filtered,
            });

            this.$swal({
              type: "success",
              text: "DID deleted successfully",
            });
            await this.loadPBXSettings();
            
            // Refresh PBX data in Vuex store to update dialer visibility
            await this.$store.dispatch('refreshPbxData');
          } catch (error) {
            console.error("Error deleting DID:", error);
            this.$swal({
              type: "error",
              text: error.body
                ? error.body.error.message
                : "Failed to delete DID",
            });
          } finally {
            this.loader = false;
          }
        }
      });
    },

    // Dialer Methods
    openDialer() {
      this.$store.dispatch("dialer/openDialer");
    },

    openDialerSettings() {
      // Open diagnostics dialog with settings form
      this.openDiagnosticsDialog();
    },

    clearDiagnosticLogs() {
      this.$store.dispatch("dialer/clearDebugLogs");
    },

    copyDiagnosticLogs() {
      const logs = this.$store.state.dialer.debugLogs || [];
      const logText = logs
        .map(
          (l) =>
            `[${l.timestamp}] [${l.level}] ${l.message} ${
              l.details ? JSON.stringify(l.details) : ""
            }`,
        )
        .join("\n");
      navigator.clipboard.writeText(logText).then(() => {
        this.$swal({
          type: "success",
          text: "Logs copied to clipboard",
          timer: 1500,
          showConfirmButton: false,
        });
      });
    },

    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },

    // Routing Helper Methods
    getRoutingStrategyLabel(strategy) {
      const found = this.routingStrategies.find((s) => s.value === strategy);
      return found ? found.label : strategy;
    },

    getRoutingColor(strategy) {
      const colors = {
        agent: "blue",
        "random-agent": "purple",
        unassigned: "grey",
      };
      return colors[strategy] || "grey";
    },

    getRoutingIcon(strategy) {
      const found = this.routingStrategies.find((s) => s.value === strategy);
      return found ? found.icon : "mdi-help-circle";
    },

    getAgentName(username) {
      const agent = this.companyAgents.find((a) => a.username === username);
      return agent ? `${agent.name} (${username})` : username;
    },

    async updateOrAddDID() {
      if (this.newDID.id) {
        // Update existing DID
        await this.updateDID();
      } else {
        // Add new DID
        await this.addDID();
      }
    },

    async updateDID() {
      if (
        !this.newDID.numberLocal ||
        this.newDID.numberLocal.toString().trim() === ""
      ) {
        this.$swal({
          type: "warning",
          text: "Please enter a DID number",
        });
        return;
      }

      // Validate routing configuration
      if (
        this.newDID.routingStrategy === "agent" &&
        !this.newDID.routingAgentUsername
      ) {
        this.$swal({
          type: "warning",
          text: "Please select an agent for routing",
        });
        return;
      }

      if (
        this.newDID.routingStrategy === "random-agent" &&
        !this.newDID.routingAllAgents &&
        (!this.newDID.routingAgentIds ||
          this.newDID.routingAgentIds.length === 0)
      ) {
        this.$swal({
          type: "warning",
          text:
            "Please select agents for routing or check 'Route to all agents'",
        });
        return;
      }

      if (!this.companyId) return;

      try {
        this.loader = true;

        // Find and update the existing DID
        const didIndex = this.existingDIDs.findIndex(
          (d) => d.id === this.newDID.id,
        );
        if (didIndex === -1) {
          throw new Error("DID not found");
        }

        const selectedCarrier = this.carriers.find(
          (c) => c.id === this.newDID.carrier,
        );
        const fullNumber = `${(this.newDID.countryCode || "").trim()}${(
          this.newDID.numberLocal || ""
        )
          .toString()
          .trim()}`.replace(/\s+/g, "");

        // Build routing configuration
        const routing = {
          type: this.newDID.routingStrategy,
          strategy: this.newDID.routingStrategy,
        };

        if (this.newDID.routingStrategy === "agent") {
          routing.agentUsername = this.newDID.routingAgentUsername;
        } else if (this.newDID.routingStrategy === "random-agent") {
          if (
            !this.newDID.routingAllAgents &&
            this.newDID.routingAgentIds &&
            this.newDID.routingAgentIds.length
          ) {
            routing.agentIds = this.newDID.routingAgentIds;
          }
        }

        if (
          this.newDID.routingFallback &&
          this.newDID.routingFallback !== this.newDID.routingStrategy
        ) {
          routing.fallback = this.newDID.routingFallback;
        }

        const updatedDID = {
          ...this.existingDIDs[didIndex],
          number: fullNumber,
          normalizedNumber: fullNumber.replace(/[^0-9+]/g, ""),
          description: this.newDID.description || "",
          carrier: selectedCarrier ? selectedCarrier.name : "",
          routing: routing,
        };

        const updatedDIDs = [...this.existingDIDs];
        updatedDIDs[didIndex] = updatedDID;

        // Save directly to PBX model
        await this.savePBXConfig({
          carrier: { ...this.carrierSettings },
          dids: updatedDIDs,
        });

        this.newDID = {
          countryCode: "+1",
          numberLocal: "",
          description: "",
          carrier: this.newDID.carrier,
          routingStrategy: "random-agent",
          routingAgentUsername: "",
          routingAgentIds: [],
          routingAllAgents: true,
          routingFallback: "",
        };

        this.$swal({
          type: "success",
          text: "DID updated successfully",
        });
        await this.loadPBXSettings();
      } catch (error) {
        console.error("Error updating DID:", error);
        this.$swal({
          type: "error",
          text: error.body ? error.body.error.message : "Failed to update DID",
        });
      } finally {
        this.loader = false;
      }
    },

    async handleTwilioComplete(twilioConfig) {
      console.log("Twilio configuration completed:", twilioConfig);

      // Reload PBX settings to show the newly configured Twilio carrier
      await this.loadPBXSettings();

      this.$swal({
        type: "success",
        title: "Twilio Connected!",
        text:
          "Your Twilio account has been successfully connected to GoDial PBX",
        timer: 3000,
      });
    },

    async deletePBXConfiguration() {
      if (!this.pbxId) {
        this.$swal({
          type: "warning",
          text: "No PBX configuration to delete",
        });
        return;
      }

      // Confirm deletion
      const result = await this.$swal({
        type: "warning",
        title: "Delete PBX Configuration?",
        text:
          "This will delete all carrier settings and DID numbers. This action cannot be undone!",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
      });

      if (!result.value) {
        return;
      }

      try {
        this.loader = true;

        // Delete the PBX configuration
        await this.$http.delete(
          `${this.$store.state.ENDPOINT}/pbx/${this.pbxId}`,
        );

        this.$swal({
          type: "success",
          title: "Deleted!",
          text: "PBX configuration has been deleted successfully",
          timer: 2000,
        });

        // Reset local state
        this.pbxId = null;
        this.pbxSettings = {
          carrier: {
            name: "",
            host: "",
            port: "",
            username: "",
            password: "",
            fromDomain: "",
            enabled: false,
          },
          dids: [],
          defaultCallingListId: null,
        };
        this.carrierSettings = { ...this.pbxSettings.carrier };
        this.existingDIDs = [];
        this.carrierEnabledLocal = false;
        this.carriers = [];
        this.defaultCallingListId = null;
      } catch (error) {
        console.error("Error deleting PBX configuration:", error);
        this.$swal({
          type: "error",
          title: "Delete Failed",
          text: error.body
            ? error.body.error.message
            : "Failed to delete PBX configuration",
        });
      } finally {
        this.loader = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   GoDial PBX - DESIGN SYSTEM
   ============================================ */

/* Container */
.pbx-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Setup Options Section - Matching welcomePopup Style */
.setup-options-card {
  border-radius: 24px !important;
  border: 1px solid #e8ecef;
  background: linear-gradient(135deg, #f8fafb 0%, #fff 50%, #f0fdf4 100%);
}

.theme--dark .setup-options-card {
  background: linear-gradient(
    135deg,
    #1e1e1e 0%,
    #252525 50%,
    #1a2e1a 100%
  ) !important;
  border-color: #333333 !important;
}

.step-badge .v-chip {
  font-size: 10px !important;
  letter-spacing: 0.8px;
  padding: 0 10px !important;
  height: 24px !important;
  border-radius: 50px !important;
}

.setup-options-title {
  font-size: 26px;
  font-weight: 700;
  color: #2c3e50;
  letter-spacing: -0.3px;
  line-height: 1.3;
}

.setup-options-subtitle {
  font-size: 14px;
  color: #5a6c7d;
  line-height: 1.6;
}

/* Setup Option Cards - Matching welcomePopup calling-option-card */
.setup-option-card {
  border-radius: 28px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: var(--v-background-base, #fff) !important;
}

.theme--dark .setup-option-card {
  background: #1e1e1e !important;
}

.setup-option-card.v-card {
  border-radius: 28px !important;
}

.setup-option-card .v-card__text {
  border-radius: 28px !important;
}

.setup-option-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: transparent;
  transition: all 0.25s ease;
}

.setup-option-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.setup-option-card:hover::before {
  height: 4px;
}

.twilio-card {
  border: 2px solid #fecaca !important;
  border-radius: 28px !important;
}

.twilio-card:hover {
  border-color: #f22f46 !important;
  box-shadow: 0 8px 24px rgba(242, 47, 70, 0.15) !important;
}

.twilio-card:hover::before {
  background: linear-gradient(90deg, #f22f46, #ff6b7a);
}

.custom-card {
  border: 2px solid #bbf7d0 !important;
  border-radius: 28px !important;
}

.custom-card:hover {
  border-color: #37cf37 !important;
  box-shadow: 0 8px 24px rgba(55, 207, 55, 0.15) !important;
}

.custom-card:hover::before {
  background: linear-gradient(90deg, #37cf37, #5dde5d);
}

/* Option Icon - Matching welcomePopup style */
.option-icon-wrapper {
  width: 70px;
  height: 70px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
}

.setup-option-card:hover .option-icon-wrapper {
  transform: scale(1.05);
}

.twilio-icon {
  background: linear-gradient(135deg, #f22f46 0%, #d91e36 100%);
  box-shadow: 0 6px 20px rgba(242, 47, 70, 0.3);
}

.custom-icon {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  box-shadow: 0 6px 20px rgba(55, 207, 55, 0.3);
}

.option-title {
  font-size: 17px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

/* Feature Items - Matching welcomePopup modern-list-item */
.option-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #5a6c7d;
  padding: 10px 12px;
  background: #f8fafb;
  border-radius: 12px;
  border: 1px solid #e8ecef;
  transition: all 0.2s ease;
}

.theme--dark .feature-item {
  background: #2d2d2d !important;
  border-color: #404040 !important;
  color: #b0b0b0 !important;
}

.twilio-card .feature-item:hover {
  background: #fff5f5;
  border-color: #fecaca;
  transform: translateX(3px);
}

.custom-card .feature-item:hover {
  background: #f0fdf4;
  border-color: #bbf7d0;
  transform: translateX(3px);
}

/* Setup Buttons - Matching welcomePopup style */
.setup-btn {
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  border-radius: 12px !important;
  height: 44px !important;
  transition: all 0.25s ease !important;
}

.twilio-card .setup-btn:hover {
  box-shadow: 0 6px 20px rgba(242, 47, 70, 0.35) !important;
  transform: translateY(-2px);
}

.custom-card .setup-btn:hover {
  box-shadow: 0 6px 20px rgba(55, 207, 55, 0.35) !important;
  transform: translateY(-2px);
}

/* OR Divider - Matching welcomePopup style */
.or-divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  padding: 20px 0;
}

.or-line {
  width: 2px;
  flex: 1;
  background: linear-gradient(
    180deg,
    transparent 0%,
    #e0e0e0 30%,
    #e0e0e0 70%,
    transparent 100%
  );
  min-height: 40px;
}

.or-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: #7f8c8d;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin: 12px 0;
}

/* OR Divider Mobile - Horizontal layout */
@media (max-width: 599px) {
  .or-divider {
    flex-direction: row;
    min-height: auto;
    padding: 24px 0;
    width: 100%;
  }

  .or-line {
    width: auto;
    height: 2px;
    min-height: auto;
    min-width: 40px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #e0e0e0 30%,
      #e0e0e0 70%,
      transparent 100%
    );
  }

  .or-circle {
    margin: 0 16px;
  }
}

/* Info Alert */
.info-alert {
  border-radius: 16px !important;
  font-size: 13px;
}

.info-alert strong {
  color: #37cf37;
}

/* Header Section */
.header-section {
  position: relative;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 16px;
  padding: 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: white;
}

.page-subtitle {
  font-size: 15px;
  opacity: 0.95;
  color: rgba(255, 255, 255, 0.9);
}

/* Feature Pills */
.feature-pills {
  margin-top: 20px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* Modern Cards */
.modern-card {
  border-radius: 16px !important;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  background: var(--v-background-base, white);
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.modern-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-4px);
}

.card-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
}

.theme--dark .card-header {
  background: linear-gradient(135deg, #2d2d2d 0%, #252525 100%) !important;
  border-color: #404040 !important;
}

.icon-badge {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.3);
}

.icon-badge-green {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.3);
}

.icon-badge-orange {
  background: linear-gradient(135deg, #f2994a 0%, #f2c94c 100%);
  box-shadow: 0 4px 12px rgba(242, 153, 74, 0.3);
}

.icon-badge-disabled {
  background: linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%);
  box-shadow: 0 4px 12px rgba(149, 165, 166, 0.2);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.card-subtitle {
  font-size: 13px;
  color: #718096;
}

/* Info Items */
.info-item {
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.info-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #718096;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
  word-break: break-all;
}

/* DID List */
.did-stats {
  display: flex;
  gap: 12px;
}

.stat-badge {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
}

.did-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.did-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.did-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.did-number {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.did-desc {
  font-size: 12px;
  color: #718096;
  margin-top: 2px;
}

/* Feature Boxes */
.feature-box {
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
  text-align: center;
  border: 1px solid #e0e0e0;
}

.feature-box-title {
  font-size: 14px;
  font-weight: 600;
  color: #757575;
  margin-bottom: 4px;
}

.feature-box-desc {
  font-size: 12px;
  color: #9e9e9e;
}

/* Coming Soon Card */
.coming-soon-card {
  opacity: 0.75;
}

/* Settings Header */
.settings-header {
  display: flex;
  align-items: center;
  padding: 16px 8px;
  border-bottom: 3px solid #37cf37;
}

.settings-heading {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

/* Settings List Container */
.settings-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.settings-list-item {
  background: white;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.settings-list-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #37cf37;
}

.settings-item-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
}

.settings-icon-wrapper {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.3);
}

.settings-icon-orange {
  background: linear-gradient(135deg, #f2994a 0%, #f2c94c 100%);
  box-shadow: 0 4px 12px rgba(242, 153, 74, 0.3);
}

.settings-item-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.settings-item-subtitle {
  font-size: 13px;
  color: #718096;
  margin: 2px 0 0 0;
}

.settings-item-content {
  background: white;
}

.settings-item-actions {
  background: #fafbfc;
  border-top: 1px solid #e0e0e0;
}

/* ==================== DIALOG STYLES ==================== */

/* Carrier Dialog */
.carrier-dialog-card {
  border-radius: 0 !important;
  overflow: hidden;
}

.carrier-dialog-header,
.did-dialog-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 24px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.carrier-dialog-header::after,
.did-dialog-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #2ba82b 0%, #37cf37 50%, #2ba82b 100%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.25);
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.header-text {
  color: white;
}

.dialog-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  font-size: 14px;
  margin: 4px 0 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

/* Dialog Content */
.carrier-dialog-content,
.did-dialog-content {
  background: #fafbfc;
}

/* Settings Sections */
.settings-section {
  background: white;
  border-radius: 0;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.section-description {
  color: #718096;
  font-size: 14px;
  margin: 0;
}

/* Input Wrappers */
.input-wrapper {
  margin-bottom: 8px;
}

.input-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  letter-spacing: 0.3px;
}

.input-label .required {
  color: #ef4444;
  margin-left: 2px;
}

.input-label .optional {
  color: #9ca3af;
  font-weight: 400;
  font-size: 12px;
}

.input-hint {
  font-size: 12px;
  color: #718096;
  margin-top: 6px;
  font-style: italic;
}

.modern-input >>> .v-input__slot {
  background: white !important;
  border-color: #e2e8f0 !important;
  transition: all 0.2s ease;
  border-radius: 0 !important;
}

.modern-input >>> .v-input__slot:hover {
  border-color: #37cf37 !important;
}

.modern-input.v-input--is-focused >>> .v-input__slot {
  border-color: #37cf37 !important;
  box-shadow: 0 0 0 3px rgba(55, 207, 55, 0.1) !important;
}

.modern-input >>> .v-text-field__details {
  border-radius: 0 !important;
}

/* Footer */
.carrier-dialog-footer,
.did-dialog-footer {
  background: white;
  border-top: 1px solid #e2e8f0;
}

.save-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.3) !important;
}

.save-btn:hover {
  box-shadow: 0 6px 16px rgba(55, 207, 55, 0.4) !important;
}

/* DID Dialog Specific */
.did-dialog-card {
  border-radius: 0 !important;
  overflow: hidden;
  max-height: 90vh;
}

.form-container {
  background: white;
}

.form-header {
  border-bottom: 2px solid #f1f5f9;
  padding-bottom: 16px;
}

.form-title {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.routing-section {
  background: #f8fafc;
  border-radius: 0;
  padding: 24px;
  border: 2px dashed #e2e8f0;
}

.routing-summary-card {
  border-radius: 0;
  border: 2px solid #bbdefb !important;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 2px solid #f1f5f9;
}

/* Table Styling */
.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.did-table {
  border-radius: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.did-table >>> .v-data-table-header {
  background: #f8fafc;
}

.did-table >>> .v-data-table-header th {
  font-weight: 600;
  color: #2d3748;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.did-table >>> tbody tr:hover {
  background-color: #f8fafc !important;
}

.number-badge {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.did-number-text {
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
}

.routing-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Dialog Transitions */
.dialog-transition-enter-active,
.dialog-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-transition-enter,
.dialog-transition-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .header-section {
    padding: 24px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .setup-options-title {
    font-size: 22px;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }

  .page-title {
    font-size: 20px;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
  }

  .feature-pill {
    font-size: 12px;
    padding: 8px 12px;
  }

  .setup-options-card {
    border-radius: 12px !important;
  }

  .setup-options-title {
    font-size: 20px;
  }

  .setup-options-subtitle {
    font-size: 13px;
  }

  .option-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .option-icon-wrapper .v-icon {
    font-size: 26px !important;
  }

  .option-title {
    font-size: 15px;
  }

  .feature-item {
    font-size: 12px;
    padding: 8px 10px;
  }

  .setup-btn {
    height: 40px !important;
    font-size: 13px !important;
  }

  /* Mobile styles for Carrier Configuration card */
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
  }

  .card-header > .d-flex:first-child {
    width: 100%;
  }

  .card-header > .d-flex:last-child {
    width: 100%;
  }

  .icon-badge {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .icon-badge .v-icon {
    font-size: 18px !important;
  }

  .card-title {
    font-size: 15px;
    line-height: 1.3;
  }

  .card-subtitle {
    font-size: 12px;
  }

  /* Reduce card padding on mobile */
  .modern-card .v-card__text {
    padding: 12px !important;
  }

  .info-item {
    padding: 10px;
  }

  .info-label {
    font-size: 10px;
  }

  .info-value {
    font-size: 13px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  /* Fix card actions buttons for mobile */
  .modern-card .v-card__actions {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px !important;
  }

  .modern-card .v-card__actions .v-btn {
    font-size: 12px !important;
    padding: 0 12px !important;
    height: 36px !important;
  }

  .modern-card .v-card__actions .v-btn .v-icon {
    font-size: 16px !important;
  }

  /* Carrier card actions - mobile layout */
  .carrier-card-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .carrier-card-actions .action-btn-mobile {
    width: 100%;
    margin: 0 !important;
  }

  .info-alert {
    font-size: 12px;
  }
}

/* Carrier card actions base styles */
.carrier-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.carrier-card-actions .action-btn-mobile {
  flex: 0 0 auto;
}

/* Debug Log Styles */
.log-entry {
  background: #f5f5f5;
  border-radius: 4px;
  border-left: 3px solid #9e9e9e;
}

.log-entry.log-error {
  background: #ffebee;
  border-left-color: #f44336;
}

.log-entry.log-warning {
  background: #fff3e0;
  border-left-color: #ff9800;
}

.log-entry.log-success {
  background: #e8f5e9;
  border-left-color: #4caf50;
}

.log-entry.log-info {
  background: #e3f2fd;
  border-left-color: #2196f3;
}

.log-entry code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 4px;
  border-radius: 2px;
  font-size: 11px;
  word-break: break-all;
}

/* ============================================
   Web Dialer Card - Enhanced Styles
   ============================================ */

.web-dialer-card {
  border: 2px solid #e0f2f1 !important;
  border-radius: 20px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.web-dialer-card:hover {
  border-color: #4caf50 !important;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.15) !important;
}

.dialer-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.dialer-status-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-weight: 500;
}

.dialer-status-bar.status-connected {
  background: linear-gradient(90deg, #e8f5e9, #c8e6c9);
  color: #2e7d32;
}

.dialer-status-bar.status-disconnected {
  background: linear-gradient(90deg, #fafafa, #f5f5f5);
  color: #9e9e9e;
}

.dialer-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-card {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.detail-card:hover {
  background: #fff;
  border-color: #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.detail-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #e3f2fd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.detail-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.detail-label {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  color: #424242;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-call-banner {
  display: flex;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  border-radius: 12px;
  color: white;
  position: relative;
  overflow: hidden;
}

.call-pulse {
  position: absolute;
  left: 16px;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.5);
  }
}

.call-info {
  display: flex;
  flex-direction: column;
}

.call-number {
  font-size: 16px;
  font-weight: 600;
}

.call-duration {
  font-size: 12px;
  opacity: 0.9;
}

.dialer-actions {
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .dialer-details-grid {
    grid-template-columns: 1fr;
  }

  .dialer-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }
}

/* Enterprise App Notice */
.enterprise-app-notice {
  background: linear-gradient(135deg, #e8f8e8 0%, #f0fdf0 100%);
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(55, 207, 55, 0.3);
  box-shadow: 0 2px 12px rgba(55, 207, 55, 0.08);
}

.enterprise-app-notice::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #37cf37 0%, #2ba82b 100%);
}

.notice-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  padding-left: 24px;
}

.notice-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(55, 207, 55, 0.25);
}

.notice-text-wrapper {
  flex: 1;
  min-width: 0;
}

.notice-title {
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notice-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.25);
}

.notice-description {
  font-size: 13px;
  color: #4a5568;
  line-height: 1.5;
  max-width: 720px;
}

.notice-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(55, 207, 55, 0.2);
  flex-shrink: 0;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-pulse {
  width: 8px;
  height: 8px;
  background: #ff9800;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
  }
}

.status-text {
  font-size: 11px;
  font-weight: 600;
  color: #ff9800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Responsive for notice */
@media (max-width: 960px) {
  .notice-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 16px;
  }

  .notice-text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .notice-title {
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
  }

  .notice-description {
    max-width: 100%;
    font-size: 12px;
  }
}

@media (max-width: 600px) {
  .enterprise-app-notice {
    border-radius: 10px;
  }

  .notice-content {
    padding: 14px;
    gap: 10px;
  }

  .notice-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .notice-title {
    font-size: 13px;
  }

  .notice-badge {
    font-size: 9px;
    padding: 2px 8px;
  }

  .notice-description {
    font-size: 12px;
  }

  .status-text {
    font-size: 10px;
  }

  .status-pulse {
    width: 7px;
    height: 7px;
  }
}

/* ============================================
   DARK THEME OVERRIDES
   ============================================ */

/* Setup Options */
.theme--dark .setup-options-title {
  color: #e0e0e0 !important;
}

.theme--dark .setup-options-subtitle {
  color: #a0a0a0 !important;
}

/* Card Title & Subtitle */
.theme--dark .card-title {
  color: #e0e0e0 !important;
}

.theme--dark .card-subtitle {
  color: #a0a0a0 !important;
}

/* Info Items */
.theme--dark .info-item {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .info-label {
  color: #a0a0a0 !important;
}

.theme--dark .info-value {
  color: #e0e0e0 !important;
}

/* DID Items */
.theme--dark .did-item {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .did-item:hover {
  background: #363636 !important;
  border-color: #4a4a4a !important;
}

.theme--dark .did-number {
  color: #e0e0e0 !important;
}

.theme--dark .did-desc {
  color: #a0a0a0 !important;
}

/* Feature Boxes */
.theme--dark .feature-box {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .feature-box-title {
  color: #b0b0b0 !important;
}

.theme--dark .feature-box-desc {
  color: #808080 !important;
}

/* Stat Badge */
.theme--dark .stat-badge {
  background: #1a2e1a !important;
  border-color: #2d4a2d !important;
  color: #4ade80 !important;
}

/* Settings */
.theme--dark .settings-heading {
  color: #e0e0e0 !important;
}

.theme--dark .settings-list-item {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.theme--dark .settings-item-header {
  background: linear-gradient(135deg, #2d2d2d 0%, #252525 100%) !important;
  border-color: #404040 !important;
}

.theme--dark .settings-item-title {
  color: #e0e0e0 !important;
}

.theme--dark .settings-item-subtitle {
  color: #a0a0a0 !important;
}

.theme--dark .settings-item-content {
  background: #1e1e1e !important;
}

.theme--dark .settings-item-actions {
  background: #252525 !important;
  border-color: #404040 !important;
}

.theme--dark .settings-section {
  background: #1e1e1e !important;
  border-color: #404040 !important;
}

.theme--dark .section-title {
  color: #e0e0e0 !important;
}

.theme--dark .section-description {
  color: #a0a0a0 !important;
}

/* Input Labels */
.theme--dark .input-label {
  color: #e0e0e0 !important;
}

.theme--dark .input-hint {
  color: #a0a0a0 !important;
}

/* Dialog Content */
.theme--dark .carrier-dialog-content,
.theme--dark .did-dialog-content {
  background: #1e1e1e !important;
}

.theme--dark .carrier-dialog-footer,
.theme--dark .did-dialog-footer {
  background: #252525 !important;
  border-color: #404040 !important;
}

.theme--dark .form-container {
  background: #1e1e1e !important;
}

.theme--dark .form-header {
  border-color: #404040 !important;
}

.theme--dark .form-title {
  color: #e0e0e0 !important;
}

.theme--dark .routing-section {
  background: #252525 !important;
  border-color: #404040 !important;
}

.theme--dark .form-actions {
  border-color: #404040 !important;
}

/* Table */
.theme--dark .table-title {
  color: #e0e0e0 !important;
}

.theme--dark .did-table {
  border-color: #404040 !important;
}

.theme--dark .did-table >>> .v-data-table-header {
  background: #2d2d2d !important;
}

.theme--dark .did-table >>> .v-data-table-header th {
  color: #e0e0e0 !important;
}

.theme--dark .did-table >>> tbody tr:hover {
  background-color: #2d2d2d !important;
}

/* Modern Input */
.theme--dark .modern-input >>> .v-input__slot {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

/* Enterprise App Notice */
.theme--dark .enterprise-app-notice {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e2e1e 100%) !important;
  border-color: rgba(55, 207, 55, 0.3) !important;
}

.theme--dark .notice-title {
  color: #e0e0e0 !important;
}

.theme--dark .notice-description {
  color: #a0a0a0 !important;
}

.theme--dark .notice-status {
  background: rgba(0, 0, 0, 0.3) !important;
  border-color: rgba(55, 207, 55, 0.3) !important;
}

/* Dialer Actions */
.theme--dark .dialer-actions {
  background: #252525 !important;
  border-color: #404040 !important;
}

/* Web Dialer Detail Cards */
.theme--dark .dialer-details-grid .detail-card {
  background: #2d2d2d !important;
  border-color: #404040 !important;
}

.theme--dark .dialer-details-grid .detail-card:hover {
  background: #353535 !important;
  border-color: #505050 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.theme--dark .dialer-details-grid .detail-icon {
  background: #1a2e1a !important;
}

.theme--dark .dialer-details-grid .detail-icon .v-icon {
  color: #4ade80 !important;
}

.theme--dark .dialer-details-grid .detail-label {
  color: #a0a0a0 !important;
}

.theme--dark .dialer-details-grid .detail-value {
  color: #e0e0e0 !important;
}
</style>
